// Constants
import { LearnMoreLinks, UncategorizedBucketId } from "@ms/uno-constants/lib/local/AppConstants";
import { DateFormat } from "@ms/uno-constants/lib/local/DateFormat";
import { EngagementTelemetryConstants } from "@ms/uno-constants/lib/local/EngagementTelemetryConstants";
import { SupportedReferenceTypes } from "@ms/uno-constants/lib/local/FileTypeConstants";
import { KeyCode } from "@ms/uno-constants/lib/local/KeyCode";
import { ControlRenderMode } from "@ms/uno-constants/lib/local/RenderModeConstants";
import { ElementIds } from "@ms/uno-constants/lib/local/UIConstants";
import { TooltipMinWidth } from "../../../../constants/StyleConstants";
// Controls
import { DirectionalHint, FocusTrapCallout, Icon, Label, Link, MessageBarType, ProgressIndicator, Toggle } from "@fluentui/react";
import projectSvg from "@ms/uno-resources/lib/local/images/logos/project-compact.svg";
import { getPlannerIconName } from "../../../../icons";
import { BaseComponent } from "../../../BaseComponent";
import { TextField, TextFieldType } from "../../../textField/TextField";
import { TooltipHost } from "../../../tooltip/TooltipHost";
import { AIGeneratedTaskSection } from "../../AIGeneratedTaskSection/AIGeneratedTaskSection";
import { AttachmentsSection } from "../../attachmentSection/AttachmentsSection";
import { Checkbox } from "../../checkbox/Checkbox";
import { ChecklistContainer } from "../../checklist/ChecklistContainer";
import { ChecklistProgressBarConfetti } from "../../checklistProgressBarConfettiAnimation/ChecklistProgressBarConfetti";
import { CompleteTaskAnimationButton } from "../../completeTaskAnimationButton/CompleteTaskAnimationButton";
import { DatePicker } from "../../datePicker/DatePicker";
import { DebouncedDropdown } from "../../dropdown/DebouncedDropdown";
import { LabelPicker } from "../../labelPicker/LabelPicker";
import { LabelTag } from "../../labelTag/LabelTag";
import { MessageBar } from "../../messageBar/MessageBar";
import { InfoSection } from "../../planInfoCallout/infoSection/InfoSection";
import { LazyRequirementsSection } from "../../requirementsSection/LazyRequirementsSection";
import { RichTextEditor } from "../../richTextEditor/RichTextEditor";
import { ExternalContextLink } from "../externalContextLink/ExternalContextLink";
import { ExternalSourceView } from "../externalSourceView/ExternalSourceView";
import { LazyAppPoweredTaskSection } from "./appPoweredTaskSection/LazyAppPoweredTaskSection";
import { PlannerTheme } from "@ms/uno-constants/lib/local/ThemeConstants";
import { Attachment } from "@ms/uno-models/lib/local/client/legacyPlanner/attachment/Attachment";
import { FormUrlType } from "@ms/uno-models/lib/local/client/legacyPlanner/form/FormUrlType";
import { PlanEntitySet } from "@ms/uno-models/lib/local/client/legacyPlanner/plan/PlanEntitySet";
import { PreviewType } from "@ms/uno-models/lib/local/client/legacyPlanner/previewType/PreviewType";
import { ProgressState } from "@ms/uno-models/lib/local/client/legacyPlanner/progressState/ProgressState";
import { PriorityName, TaskKind } from "@ms/uno-models/lib/local/client/legacyPlanner/task/ITask";
import { ChecklistCompletionRequirement } from "@ms/uno-models/lib/local/client/legacyPlanner/task/mandatoryRequirements/ChecklistCompletionRequirement";
import { CompletionRequirements } from "@ms/uno-models/lib/local/client/legacyPlanner/task/mandatoryRequirements/CompletionRequirements";
import { RecurrencePattern } from "@ms/uno-models/lib/local/client/legacyPlanner/task/recurrence/RecurrencePattern";
import { RecurrenceSchedule } from "@ms/uno-models/lib/local/client/legacyPlanner/task/recurrence/RecurrenceSchedule";
import { TaskAccessContext } from "@ms/uno-models/lib/local/client/legacyPlanner/task/TaskAccessContext";
import { AttachmentPermissions, isAttachmentPermissionDisabled } from "@ms/uno-models/lib/local/client/permissions/AttachmentPermissions";
import { ChecklistPermissions } from "@ms/uno-models/lib/local/client/permissions/ChecklistPermissions";
import { MandatoryChecklistTogglePermissions } from "@ms/uno-models/lib/local/client/permissions/MandatoryChecklistTogglePermissions";
import { TaskAttachmentRowItem } from "@ms/uno-models/lib/local/client/taskAttachment/TaskAttachmentRowItem";
import { GroupWorkLoad } from "@ms/uno-models/lib/local/client/unifiedGroup/GroupWorkLoad";
import { ContainerType } from "@ms/uno-models/lib/local/service/graph-legacy/container/ContainerType";
import { SpecifiedCompletionRequirements } from "@ms/uno-models/lib/local/service/graph-legacy/task/completionRequirements/SpecifiedCompletionRequirements";
import { RecurrenceDropdownType } from "@ms/uno-models/lib/local/service/graph-legacy/task/recurrence/RecurrenceDropdownType";
import { RecurrencePatternType } from "@ms/uno-models/lib/local/service/graph-legacy/task/recurrence/RecurrencePatternType";
import { ContentType } from "@ms/uno-models/lib/local/service/graph/general/ContentType";
// Styles
import { dismissSmallIconProps, editIconProps, infoSmallIconProps, recurrenceIconProps, sparkleRegularIconProps, tagIconProps } from "../../../../icons/icons.styles";
import { getCalloutStyles } from "../../../tooltip/Tooltip.styles";
import { getLabelEditorStyleFunction } from "../../labelEditor/LabelEditor.styles";
import { getLabelPickerStyleFunction } from "../../labelPicker/LabelPicker.styles";
import { customAttachmentSectionStyles, customMessageBarStyles, customRichTextEditorStyles, getClassNames, getStyles } from "./TaskEditor.styles";
// Telemetry
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
// Utilities
import * as StringHelper from "@fluentui/utilities";
import { css } from "@fluentui/utilities/lib/css";
import { DayOfWeek } from "@ms/uno-constants/lib/local/DateConstants";
import { ErrorUtilities } from "@ms/uno-errors/lib/local/utilities/ErrorUtilities";
import { getRecurrenceMonthIndexFromMoment } from "@ms/uno-models/lib/local/client/legacyPlanner/task/recurrence/utilities/RecurrencePatternUtilities";
import { areApprovalCompletionRequirementSet, areChecklistCompletionRequirementSet, areFormsCompletionRequirementSet, getAppPoweredTaskAttachmentDetails } from "@ms/uno-models/lib/local/client/legacyPlanner/utilities/CompletionRequirementsUtils";
import { getILabelArray } from "@ms/uno-models/lib/local/utilities/LabelUtilities";
import { Strings } from "@ms/uno-resources/lib/local/CultureUtilities";
import { compareOrderHints } from "@ms/uno-utilities/lib/local/Comparators";
import { FormatJSDateLongForm, getDayOfWeekFromMomentIndex, InLastMinute, InPastFewDays, InPastFewHours, InPastFewMinutes, InPastHour, IsToday, IsYesterday, ParseDateFromString, ToUTC10am } from "@ms/uno-utilities/lib/local/DateUtilities";
import { withDndContext } from "@ms/uno-utilities/lib/local/DndUtilities";
import { KeyboardUtilities } from "@ms/uno-utilities/lib/local/KeyboardUtilities";
import { LocalIdGenerator } from "@ms/uno-utilities/lib/local/LocalIdGenerator";
import { generateOrderHintBetween } from "@ms/uno-utilities/lib/local/OrdinalUtilities";
import compact from "lodash/compact";
import find from "lodash/find";
import forEach from "lodash/forEach";
import indexOf from "lodash/indexOf";
import isEmpty from "lodash/isEmpty";
import size from "lodash/size";
import throttle from "lodash/throttle";
import values from "lodash/values";
import moment from "moment";
import * as React from "react";
import { Suspense } from "react";
import { getPriorityIconProps, getProgressIconProps } from "../../../../icons/IconUtilities";
import { convertUrlsToRenderableLinks } from "../../../../utilities/LinkifyUtilities";
import { checkIfPlanHasMeetingContext } from "../../../../utilities/PlanUtilities";
//Svgs
import taskInCompleteIcon from "@ms/uno-resources/lib/local/images/task-incomplete.svg";
import { fontColorThemePrimary } from "../../../../utilities/Mixin.styles";
/** Constants for number of comments to show by default */ const COMMENTS_COLLAPSED_THRESHOLD = 5;
/** Throttle wait time for task description update in milliseconds */ const ThrottleValueForTaskDescriptionUpdate = 5000;
/** The notes description field max height (editor preview height) after which there is a show more button */ const NotesPreviewMaxHeight = "146";
var ShowOnCardTypes;
/**
 * Enum representing the types of ShowOnCard checkbox types
 */ (function(ShowOnCardTypes) {
    /** When checklist is triggered with a show on card change */ ShowOnCardTypes[ShowOnCardTypes["Checklist"] = 0] = "Checklist";
    /** When description is triggered with a show on card change */ ShowOnCardTypes[ShowOnCardTypes["Description"] = 1] = "Description";
    /** When attachment/reference is triggered with a show on card change */ ShowOnCardTypes[ShowOnCardTypes["Attachment"] = 2] = "Attachment";
})(ShowOnCardTypes || (ShowOnCardTypes = {}));
export var ChecklistWithCompletionRequirementsState;
(function(ChecklistWithCompletionRequirementsState) {
    /** initial state */ ChecklistWithCompletionRequirementsState[ChecklistWithCompletionRequirementsState["None"] = 0] = "None";
    /** When checklist items are completed and task can be completed */ ChecklistWithCompletionRequirementsState[ChecklistWithCompletionRequirementsState["ChecklistCompleted"] = 1] = "ChecklistCompleted";
    /** When checklist items are not completed and task can't be completed */ ChecklistWithCompletionRequirementsState[ChecklistWithCompletionRequirementsState["ChecklistNotCompleted"] = 2] = "ChecklistNotCompleted";
    /** When checklist items are not completed but user changed the task from completed to not completed state */ ChecklistWithCompletionRequirementsState[ChecklistWithCompletionRequirementsState["ChecklistDisabled"] = 3] = "ChecklistDisabled";
})(ChecklistWithCompletionRequirementsState || (ChecklistWithCompletionRequirementsState = {}));
/**
 * Task details editor control
 */ class TaskEditor extends BaseComponent {
    /** @inheritdoc */ componentDidMount() {
        const { plan, planDetails } = this.props;
        if (plan && planDetails) {
            this.logUserAccessEditorOpen(plan, planDetails);
        }
    }
    /** @inheritdoc */ componentDidUpdate(prevProps) {
        const { plan, planDetails, taskDetails, config, task } = this.props;
        if (planDetails == null || plan == null) {
            return;
        }
        if (prevProps.plan == null || prevProps.planDetails == null) {
            // Plan or plan details have just loaded for the given task. Log the open plan now that we have the required info
            this.logUserAccessEditorOpen(plan, planDetails);
        } else if (prevProps.plan != null && plan != null && prevProps.plan.id !== plan.id) {
            // If the contained plan changed, we'll want to log it as a new open
            this.logUserAccessEditorOpen(plan, planDetails);
        }
        // Since we handle the value of the title textbox in the state we need to see if the new task title has changed and put it in the state
        if (prevProps.task.title !== this.props.task.title) {
            this.setState((prevState)=>{
                return {
                    updatedTitle: this.props.task.title
                };
            });
        }
        // Apply description update when the description has changed and user is NOT in edit mode
        const isDescriptionUpdated = config.enableRichTextEditor ? prevProps.taskDetails?.notes !== taskDetails?.notes : prevProps.taskDetails?.description !== taskDetails?.description;
        if (taskDetails != null && isDescriptionUpdated && !this.state.showDescriptionInEditMode) {
            this.setState(()=>{
                return {
                    updatedNotesContent: taskDetails.notes?.content,
                    updatedDescription: taskDetails.description,
                    descriptionWithHyperlinks: taskDetails.description ? convertUrlsToRenderableLinks(taskDetails.description) : null
                };
            });
        }
        if (task.specifiedCompletionRequirements & SpecifiedCompletionRequirements.ChecklistItemsCompletion) {
            if (prevProps.task.numberOfActiveChecklistItems !== task.numberOfActiveChecklistItems && task.numberOfActiveChecklistItems === 0) {
                this.setState({
                    // When all checklist items are completed, complete task button should be enabled and
                    // tooltip should be shown to inform user that they can complete the task. Checkbox should be enabled
                    isCompletionRequirementsInfoCalloutVisible: true
                });
            }
            if (prevProps.task.numberOfActiveChecklistItems !== task.numberOfActiveChecklistItems && prevProps.task.numberOfActiveChecklistItems === 0) {
                // When user uncompleted the task and uncheck at the checklist item, the user needs to see the tooltip again.
                // Tooltip will inform user that they need to complete the checklist items to complete the task.
                // Checkbox should be disabled.
                this.setState({
                    isCompletionRequirementsInfoCalloutVisible: true
                });
            }
        }
    }
    /**
     * This function returns whether or not to render the start date field in Task Editor
     * @param {ITaskEditorConfig} config - The task editor config.
     * @param {TaskKind} taskKind - The kind of task.
     * @returns {boolean} whether to render start date field in Task Editor.
     */ showStartDate(config, taskKind) {
        return taskKind !== TaskKind.SharedConsumer && (config.enableExtraFieldsForPersonalTasks || taskKind !== TaskKind.Todo);
    }
    /**
     * This function returns whether or not to render the progress field in Task Editor
     * @param {TaskKind} taskKind - The kind of task.
     * @param {ITaskEditorConfig} config - The task editor config.
     * @returns {boolean} whether to render progress field in Task Editor.
     */ showProgress(taskKind, config) {
        return taskKind === TaskKind.Shared || config.enableExtraFieldsForPersonalTasks && taskKind === TaskKind.Todo;
    }
    /**
     * Name of the component
     */ name() {
        return "TaskEditor";
    }
    /**
     * Render TaskEditor component
     */ renderComponent() {
        const { task, config } = this.props;
        const classNames = this.getClassNames();
        return /*#__PURE__*/ React.createElement("div", {
            className: classNames.root,
            onKeyDown: this.stopPropagation
        }, /*#__PURE__*/ React.createElement("div", {
            className: "removeMargin"
        }, this.renderPlanLink(), this.renderTaskTitleRow(), this.renderLastModifiedSection(), this.renderAppPoweredTaskSection(), config.enableAPTSection && task.isAppPoweredTask() ? this.renderAPTDisabledSection() : this.renderMainTaskEditorSection()));
    }
    /**
     * Renders the main task editor section.
     *
     * @returns The JSX element representing the main task editor section.
     */ renderMainTaskEditorSection() {
        const { callbacks, config, children } = this.props;
        const { renderOnedrivePicker, renderCustomRecurrenceDialog, renderAddFormRequirementDialog, renderRemoveFormConfirmationDialog, renderFormResponseViewDialog, renderEditFormDialog, renderCheckUnsavedApprovalDialog } = callbacks;
        // The setup for private tasks is such that the flight 'enableRichTextEditor' is always treated to be 'true'.
        // If we try to switch this flight to 'off' then the 'Description' section for private tasks will render empty.
        // It will not accept any input from the user.
        return /*#__PURE__*/ React.createElement(React.Fragment, null, this.renderOpenExternalLinkedContextSection(), this.renderExternalSourceSection(), this.renderMessageBannerIfProjectTask(), this.renderAssignmentSection(), this.renderLabelPicker(), this.renderFieldDropDownsSection(), config.enableRichTextEditor ? this.renderRichTextEditor() : this.renderDescriptionSection(), children, this.renderChecklistSection(), config.enableGoalsNameInTaskEditor && this.renderGoalsSection(), this.renderMissingFieldsMessageBar(), this.renderFunctionalityWarnings(), this.renderAttachments(), this.renderRequirementsSection(), this.renderCommentsSection(), this.renderAIGeneratedTaskSection(), renderOnedrivePicker(), renderCustomRecurrenceDialog(), renderAddFormRequirementDialog(), renderRemoveFormConfirmationDialog(), renderFormResponseViewDialog(), renderEditFormDialog(), renderCheckUnsavedApprovalDialog());
    }
    /**
     * Renders the APT disabled section.
     *
     * @param classNames - The class names for styling the section.
     * @returns The JSX element representing the APT disabled section.
     */ renderAPTDisabledSection() {
        const { config } = this.props;
        return /*#__PURE__*/ React.createElement("div", {
            className: "AppPoweredTasksEditorView"
        }, this.renderAssignmentSection(), this.renderLabelPicker(), this.renderFieldDropDownsSection(), config.enableRichTextEditor ? this.renderRichTextEditor() : this.renderDescriptionSection());
    }
    /**
     * Render the Missing Field MessageBar
     * @param {ITask} task
     * @returns { null | JSX.Element } - Returns null if its a personal task, JSX element otherwise
     */ renderMissingFieldsMessageBar() {
        const { task } = this.props;
        if (task.kind !== TaskKind.Todo || !this.props.config.enableMissingFieldsMessageForPersonalTasks) {
            return null;
        }
        return /*#__PURE__*/ React.createElement("div", {
            className: "messageBarWrapper",
            role: "status"
        }, /*#__PURE__*/ React.createElement(MessageBar, {
            role: "none"
        }, StringHelper.format(Strings.TaskEditor_PersonalTasksMissingFieldsMessageText, Strings.SharedComponents_ToDoAppTitle), /*#__PURE__*/ React.createElement(Link, {
            href: LearnMoreLinks.missingFields
        }, Strings.SharedComponents_LearnMoreLink)));
    }
    /**
     * Render fieldDropDowns Section
     * @param classNames classnames associated with the computed styles
     */ renderFieldDropDownsSection() {
        const { task, bucket, callbacks } = this.props;
        const { permissions } = this.props.config;
        const { traceLogger } = this.context;
        const classNames = this.getClassNames();
        const taskProgressState = callbacks.convertPercentCompleteToProgressState(task.percentComplete);
        const taskPriorityName = callbacks.convertPriorityToPriorityName(task.priority);
        const isDisabled = !permissions.canEdit || !permissions.canUpdateBucket;
        const dateStrings = {
            months: moment.months(),
            shortMonths: moment.monthsShort(),
            days: moment.weekdays(),
            shortDays: moment.weekdaysShort(),
            goToToday: Strings.SharedComponents_CalendarPickerTodayLabel,
            prevMonthAriaLabel: Strings.SharedComponents_PrevMonthAriaLabel,
            nextMonthAriaLabel: Strings.SharedComponents_NextMonthAriaLabel,
            prevYearAriaLabel: Strings.SharedComponents_PrevYearAriaLabel,
            nextYearAriaLabel: Strings.SharedComponents_NextYearAriaLabel,
            invalidInputErrorMessage: Strings.SharedComponents_InvalidDateErrorMessage,
            prevYearRangeAriaLabel: Strings.SharedComponents_PrevYearRangeAriaLabel,
            nextYearRangeAriaLabel: Strings.SharedComponents_NextYearRangeAriaLabel
        };
        const currentBucketTitle = bucket?.title ?? undefined;
        return /*#__PURE__*/ React.createElement("div", {
            className: classNames.dropdowns
        }, /*#__PURE__*/ React.createElement("div", {
            className: "removeMargin"
        }, (task.kind !== TaskKind.Todo || this.props.config.enableExtraFieldsForPersonalTasks) && task.kind !== TaskKind.SharedConsumer && /*#__PURE__*/ React.createElement("div", {
            className: "dropdownTuple",
            title: currentBucketTitle
        }, /*#__PURE__*/ React.createElement(DebouncedDropdown, {
            className: "taskBucket",
            title: !permissions.canEdit ? Strings.SharedComponents_ReadonlyFieldPermissionsTooltip : undefined,
            label: Strings.TaskEditor_TaskBucketLabel,
            options: this.getBucketList(),
            onChange: this.onBucketChanged,
            selectedKey: task.bucketId || UncategorizedBucketId,
            onRenderOption: (props)=>{
                return /*#__PURE__*/ React.createElement("div", {
                    className: css("taskEditor_taskBucketItem", classNames.bucketRecurrenceDropdown)
                }, /*#__PURE__*/ React.createElement("div", null, props.text));
            },
            disabled: isDisabled,
            debounceInterval: TaskEditor.debounceDropdownTaskUpdateInterval
        })), this.showProgress(task.kind, this.props.config) && /*#__PURE__*/ React.createElement("div", {
            className: "dropdownTuple",
            title: callbacks.convertPercentCompleteToProgressText(task.percentComplete)
        }, /*#__PURE__*/ React.createElement(DebouncedDropdown, {
            className: "taskProgress",
            label: Strings.TaskEditor_TaskProgressLabel,
            title: !permissions.canUpdateProgress ? Strings.SharedComponents_ReadonlyFieldPermissionsTooltip : undefined,
            options: this.getProgressList(task),
            onChange: this.onProgressChanged,
            selectedKey: taskProgressState,
            onRenderTitle: (options)=>{
                const option = options[0];
                const iconProps = getProgressIconProps(this.props.theme, ProgressState[option.key], traceLogger);
                return /*#__PURE__*/ React.createElement("div", {
                    className: css("taskEditor_taskProgressItem", classNames.dropdownsItems)
                }, /*#__PURE__*/ React.createElement("div", null, /*#__PURE__*/ React.createElement("div", {
                    className: "icon"
                }, /*#__PURE__*/ React.createElement(Icon, iconProps)), /*#__PURE__*/ React.createElement("div", {
                    className: "textValue"
                }, option.text)));
            },
            onRenderOption: (props)=>{
                const iconProps = getProgressIconProps(this.props.theme, ProgressState[props.key], traceLogger);
                return /*#__PURE__*/ React.createElement("div", {
                    className: css("taskEditor_taskProgressItem", classNames.dropdownsItems)
                }, /*#__PURE__*/ React.createElement("div", null, /*#__PURE__*/ React.createElement("div", {
                    className: "icon"
                }, /*#__PURE__*/ React.createElement(Icon, iconProps)), /*#__PURE__*/ React.createElement("div", {
                    className: "textValue"
                }, props.text)));
            },
            disabled: !permissions.canUpdateProgress,
            debounceInterval: TaskEditor.debounceDropdownTaskUpdateInterval
        })), /*#__PURE__*/ React.createElement("div", {
            className: "dropdownTuple",
            title: callbacks.convertPriorityToPriorityText(task.priority)
        }, /*#__PURE__*/ React.createElement(DebouncedDropdown, {
            className: "taskPriority",
            title: !permissions.canUpdatePriority ? Strings.SharedComponents_ReadonlyFieldPermissionsTooltip : undefined,
            label: Strings.Task_PriorityFieldText,
            options: this.getPriorityList(task),
            onChange: this.onPriorityChanged,
            selectedKey: taskPriorityName,
            onRenderTitle: (options)=>{
                const option = options[0];
                const iconProps = this.getPriorityIconProps(PriorityName[option.key]);
                return /*#__PURE__*/ React.createElement("div", {
                    className: css("taskEditor_taskPriorityItem", classNames.dropdownsItems)
                }, /*#__PURE__*/ React.createElement("div", null, /*#__PURE__*/ React.createElement("div", {
                    className: "icon"
                }, /*#__PURE__*/ React.createElement(Icon, iconProps)), /*#__PURE__*/ React.createElement("div", {
                    className: "textValue"
                }, option.text)));
            },
            onRenderOption: (props)=>{
                const iconProps = this.getPriorityIconProps(PriorityName[props.key]);
                return /*#__PURE__*/ React.createElement("div", {
                    className: css("taskEditor_taskPriorityItem", classNames.dropdownsItems)
                }, /*#__PURE__*/ React.createElement("div", null, /*#__PURE__*/ React.createElement("div", {
                    className: "icon"
                }, /*#__PURE__*/ React.createElement(Icon, iconProps)), /*#__PURE__*/ React.createElement("div", {
                    className: "textValue"
                }, props.text)));
            },
            disabled: !permissions.canEdit || !permissions.canUpdatePriority,
            debounceInterval: TaskEditor.debounceDropdownTaskUpdateInterval
        })), this.showStartDate(this.props.config, task.kind) && /*#__PURE__*/ React.createElement("div", {
            className: "dropdownTuple",
            title: task.startDate == null ? Strings.TaskEditor_TaskStartDatePlaceholder : undefined
        }, /*#__PURE__*/ React.createElement(DatePicker, {
            calloutProps: {
                ariaLabel: Strings.TaskEditor_SelectStartDateAriaLabel
            },
            label: Strings.Task_StartDateFieldText,
            title: !permissions.canEdit ? Strings.SharedComponents_ReadonlyFieldPermissionsTooltip : undefined,
            isMonthPickerVisible: true,
            onSelectDate: this.onStartDateChanged,
            placeholder: task.startDate == null ? Strings.TaskEditor_TaskStartDatePlaceholder : "",
            strings: dateStrings,
            value: task.startDate == null ? undefined : task.startDate.toDate(),
            formatDate: FormatJSDateLongForm,
            parseDateFromString: ParseDateFromString,
            ariaLabel: task.startDate == null ? Strings.Task_StartDateFieldText : undefined,
            allowTextInput: true,
            borderless: true,
            today: new Date(),
            firstDayOfWeek: moment.localeData().firstDayOfWeek(),
            disabled: !permissions.canEdit || !permissions.canUpdateStartDate,
            appTheme: this.props.theme,
            textField: {
                iconProps: {
                    id: ElementIds.TaskDetailsStartDateCalendarIcon
                }
            }
        })), /*#__PURE__*/ React.createElement("div", {
            className: "dropdownTuple",
            title: task.dueDate == null ? Strings.TaskEditor_TaskEndDatePlaceholder : undefined
        }, /*#__PURE__*/ React.createElement(DatePicker, {
            calloutProps: {
                ariaLabel: Strings.TaskEditor_SelectEndDateAriaLabel
            },
            label: Strings.Task_DueDateFieldText,
            title: !permissions.canUpdateDueDate ? Strings.SharedComponents_ReadonlyFieldPermissionsTooltip : undefined,
            isMonthPickerVisible: true,
            onSelectDate: this.onEndDateChanged,
            placeholder: task.dueDate == null ? Strings.TaskEditor_TaskEndDatePlaceholder : "",
            strings: dateStrings,
            value: task.dueDate == null ? undefined : task.dueDate.toDate(),
            formatDate: FormatJSDateLongForm,
            parseDateFromString: ParseDateFromString,
            ariaLabel: task.dueDate == null ? Strings.Task_DueDateFieldText : undefined,
            allowTextInput: true,
            borderless: true,
            today: new Date(),
            firstDayOfWeek: moment.localeData().firstDayOfWeek(),
            disabled: !permissions.canEdit || !permissions.canUpdateDueDate,
            appTheme: this.props.theme
        })), task.kind !== TaskKind.Todo && task.kind !== TaskKind.SharedConsumer && !(task.kind === TaskKind.Shared && this.props.config.enableRecurringTasks) && /*#__PURE__*/ React.createElement("div", {
            className: "dropdownTuple"
        }), task.kind === TaskKind.Template && /*#__PURE__*/ React.createElement("div", {
            className: "dropdownTuple"
        }), this.renderTaskRecurrenceDropdown(classNames)));
    }
    /**
     * Render the assignment section
     * @param classNames classnames associated with the computed styles
     */ renderAssignmentSection() {
        const { task, callbacks, config } = this.props;
        const classNames = this.getClassNames();
        if (task.kind !== TaskKind.Shared && task.kind !== TaskKind.SharedConsumer) {
            // Publishable templates or personal tasks do not have assignments
            return null;
        }
        const assignmentControl = callbacks.renderAssignmentControl(config.permissions, config.containerPermissions);
        return /*#__PURE__*/ React.createElement("div", {
            className: css(classNames.assignedToUsers, "passiveAlt_0"),
            ref: this.assignmentControlRef
        }, assignmentControl);
    }
    /**
     * Render the section that shows an external task source
     */ renderExternalSourceSection() {
        return /*#__PURE__*/ React.createElement(ExternalSourceView, {
            task: this.props.task,
            plan: this.props.plan,
            getUserById: this.props.callbacks.getUserById
        });
    }
    /**
     * Render the open external linked context section for certain plans such as Roster plan
     * Note: Currently this is scoped down to Meeting Notes and Loop Page only
     * Todo: Bug #7683031 - depending on the readiness of external source on task level for tasks in meeting plans,
     *                      either revert temp fix below or remove this section overall
     * @param plan Plan object for the task
     * @param classNames classnames associated with the computed styles
     */ renderOpenExternalLinkedContextSection() {
        const { planDetails, plan, task, container } = this.props;
        // const classNames = this.getClassNames();
        // This is a temp fix until Meeting plan's tasks have task level external source
        // When a plan has meeting context and tasks within the plan does not have creationSource, show the link to meeting note
        const shouldForcePlanContextMeetingNotes = plan && planDetails ? checkIfPlanHasMeetingContext(plan, planDetails) && isEmpty(task.creationSource.external) : false;
        if (!shouldForcePlanContextMeetingNotes || !plan || !planDetails || task.isProjectTask()) {
            return null;
        }
        return /*#__PURE__*/ React.createElement(ExternalContextLink, {
            plan: plan,
            planDetails: planDetails,
            planContainer: container
        });
    }
    /**
     * Render the Label Picker
     * @param classNames classnames associated with the computed styles
     */ renderLabelPicker() {
        const { planDetails, planLabelCounts, task, config, theme } = this.props;
        const classNames = this.getClassNames();
        if (!planDetails?.categories) {
            return null;
        }
        if (task.kind === TaskKind.Todo || task.kind === TaskKind.SharedConsumer) {
            // Personal or shared consumer tasks do not have labels
            return null;
        }
        const copilotWorkflowStatusLabel = this.props.callbacks.getCopilotWorkflowStatusLabel();
        return /*#__PURE__*/ React.createElement("div", {
            className: classNames.labelPickerArea
        }, /*#__PURE__*/ React.createElement(Icon, {
            ...tagIconProps,
            title: Strings.TaskEditor_LabelTooltip
        }), /*#__PURE__*/ React.createElement(LabelPicker, {
            appliedLabels: task.appliedCategories,
            customLabels: copilotWorkflowStatusLabel ? [
                copilotWorkflowStatusLabel
            ] : [],
            editableLabelStrings: {
                addLabelTooltip: Strings.EditableLabel_AddLabelTooltip,
                editButtonAriaLabel: Strings.EditableLabel_EditButtonAriaLabel,
                editLabelButtonAriaLabel: Strings.EditableLabel_EditLabelButtonAriaLabel,
                editLabelTooltip: Strings.EditableLabel_EditLabelTooltipText,
                removeButtonAriaLabel: Strings.EditableLabel_RemoveButtonAriaLabel
            },
            labelEditorStrings: {
                editLabel: Strings.LabelEditor_EditLabel,
                editLabelNameAriaLabel: Strings.LabelEditor_EditLabelNameAriaLabel
            },
            labelPickerStrings: {
                addLabel: Strings.LabelPicker_AddLabel,
                allLabelsAdded: Strings.LabelPicker_AllLabelsAdded,
                noResultsFound: Strings.LabelPicker_NoResultsFound,
                searchForLabel: Strings.LabelPicker_SearchForLabel,
                searchForLabelAriaLabel: Strings.LabelPicker_SearchForLabelAriaLabel
            },
            labels: getILabelArray(planDetails.categories, planLabelCounts),
            onChangeLabelName: this.onChangeLabelName,
            onChangeSelection: this.onChangeCategorySelection,
            isReadOnly: !config.permissions.canUpdateAppliedLabels,
            styles: getLabelPickerStyleFunction(theme),
            labelEditorStyles: getLabelEditorStyleFunction(theme),
            editableLabelIcons: {
                edit: editIconProps,
                cancel: dismissSmallIconProps
            }
        }));
    }
    /**
     * Render the comments section
     * @param classNames classnames associated with the computed styles
     */ renderCommentsSection() {
        const { task, config } = this.props;
        const classNames = this.getClassNames();
        if (!config.enableComments) {
            return null;
        }
        if (task.kind !== TaskKind.Shared) {
            // Only shared tasks have comment threads attached to them
            return null;
        }
        const threadView = this.props.callbacks.renderCommentThreadViewControl(config.permissions, COMMENTS_COLLAPSED_THRESHOLD);
        return /*#__PURE__*/ React.createElement("div", {
            className: classNames.commentsSection
        }, threadView);
    }
    /**
     * Render the AI generated tasks section
     */ renderAIGeneratedTaskSection() {
        const { tenantFeedbackSettings, appContext, task, isAITask, taskCreationDate } = this.props;
        const formattedDate = taskCreationDate.format(DateFormat.MonthDateTimeFormat);
        if (!isAITask) {
            return null;
        }
        return /*#__PURE__*/ React.createElement(AIGeneratedTaskSection, {
            icon: sparkleRegularIconProps,
            primaryText: Strings.TaskEditor_TaskCreatedWithAI,
            secondaryText: formattedDate,
            taskId: task.id,
            tenantFeedbackSettings: tenantFeedbackSettings,
            appContext: appContext
        });
    }
    renderGoalsSection() {
        const { task, callbacks } = this.props;
        const classNames = this.getClassNames();
        if (!task.goalIds?.length) {
            return null;
        }
        // Process goals data
        const processedGoals = task.goalIds.map((goalId)=>{
            const goal = callbacks.getGoalById(goalId);
            const goalName = goal?.displayName || "";
            const isAIGenerated = goal?.isAIGenerated || false;
            return {
                goalId,
                goalName,
                isAIGenerated
            };
        });
        return /*#__PURE__*/ React.createElement("div", {
            className: classNames.goalsSection
        }, /*#__PURE__*/ React.createElement("label", {
            className: "goalsSectionLabel"
        }, Strings.ProjectHomeView_GoalHeader), processedGoals.map(({ goalId, goalName, isAIGenerated })=>/*#__PURE__*/ React.createElement(TooltipHost, {
                key: goalId,
                content: Strings.TaskEditor_GoalNameLabel,
                directionalHint: DirectionalHint.topCenter
            }, /*#__PURE__*/ React.createElement("div", {
                className: classNames.goalItem
            }, isAIGenerated ? /*#__PURE__*/ React.createElement(Icon, {
                iconName: getPlannerIconName("plannerAITrophy")
            }) : /*#__PURE__*/ React.createElement(Icon, {
                iconName: getPlannerIconName("plannerGoals"),
                className: classNames.goalIcon
            }), /*#__PURE__*/ React.createElement("span", {
                className: classNames.goalText
            }, goalName)))));
    }
    /**
     * Renders functionality warnings to the user in the case where some functionality is limited due to container capabilities / etc
     */ renderFunctionalityWarnings() {
        const { plan } = this.props;
        if (plan == null) {
            return null;
        }
        if (plan.container.containerType === ContainerType.Roster) {
            return /*#__PURE__*/ React.createElement("div", {
                className: "functionalityWarning"
            }, /*#__PURE__*/ React.createElement(MessageBar, {
                messageBarType: MessageBarType.info,
                isMultiline: true,
                styles: customMessageBarStyles(this.props.theme)
            }, Strings.TaskEditor_FunctionalityWarning, /*#__PURE__*/ React.createElement(Link, {
                href: LearnMoreLinks.lightWeightPlans,
                target: "_blank",
                rel: "noopener noreferrer"
            }, Strings.SharedComponents_LearnMoreLink)));
        }
        return null;
    }
    /**
     * Render the task title row with complete button
     * @param task The task object the editor is using
     * @param classNames classnames associated with the computed styles
     */ renderTaskTitleRow() {
        const classNames = this.getClassNames();
        const { task } = this.props;
        const { limits, permissions } = this.props.config;
        const titleClassName = css(classNames.taskTitle, {
            completed: task.isComplete()
        });
        return /*#__PURE__*/ React.createElement("div", {
            className: titleClassName,
            id: "taskDetailsTitleSectionId"
        }, this.renderCompleteButton(task.isComplete(), classNames), /*#__PURE__*/ React.createElement(TextField, {
            autoFocus: false,
            title: !permissions.canEditTitle ? Strings.SharedComponents_ReadonlyFieldPermissionsTooltip : undefined,
            className: "edit",
            placeholder: Strings.TaskEditor_TaskNamePlaceholder,
            maxLength: limits.maxTitleLength,
            value: this.state.updatedTitle,
            onChange: (event, newValue)=>{
                this.onTitleChanged(newValue);
            },
            onKeyUp: this.taskTitleKeyPress,
            onBlur: this.taskTitleUpdate,
            tabIndex: 0,
            styleType: TextFieldType.Title,
            ariaLabel: Strings.TaskEditor_TaskNameAriaLabel,
            disabled: !permissions.canEdit || !permissions.canEditTitle
        }));
    }
    /** Private method that returns a JSX Element for displaying a completion requirements info callout.
     * @param classNames - The class names for the task editor.
     * @returns The JSX Element for the completion requirements info callout.
     */ renderCompletionRequirementsInfoCallout(classNames) {
        if (!this.state.isCompletionRequirementsInfoCalloutVisible) {
            return null;
        }
        const { task } = this.props;
        let target = null;
        let tooltipText = "";
        if (task.isComplete()) {
            target = this.checklistAsteriskRef;
            tooltipText = Strings.TaskEditor_ShowMandatoryChecklistIncompleteLabel;
        } else {
            if (task.numberOfActiveChecklistItems === 0) {
                target = this.completionRequirementCalloutRef;
                tooltipText = Strings.TaskEditor_ShowCanCompleteTaskWithRequirementsInfoLabel;
            } else {
                target = this.completionRequirementCalloutRef;
                tooltipText = Strings.TaskEditor_ShowShouldCompleteRequirementsInfoLabel;
            }
        }
        return /*#__PURE__*/ React.createElement(FocusTrapCallout, {
            isBeakVisible: true,
            calloutMinWidth: TooltipMinWidth,
            gapSpace: 0,
            focusTrapProps: {
                isClickableOutsideFocusTrap: true
            },
            doNotLayer: true,
            target: target,
            setInitialFocus: true,
            className: classNames.mandatoryChecklistInfoTooltip,
            onDismiss: this.toggleCompletionRequirementsInfoCallout,
            styles: getCalloutStyles(this.props.theme)
        }, /*#__PURE__*/ React.createElement(InfoSection, {
            description: tooltipText
        }));
    }
    /**
     * Render the complete button
     * @param { boolean } isTaskCompleted Indicates whether the task is completed
     * @param classNames The class names for the task editor.
     * @returns The JSX Element for the CompleteButton
     */ renderCompleteButton(isTaskCompleted, classNames) {
        const { config, task, theme, taskDetails, isAITask } = this.props;
        const enableCompletionRequirements = config.enableMandatoryCheckList && areChecklistCompletionRequirementSet(task, taskDetails);
        const enableFormsCompletionRequirements = config.enableFormsRequirement && areFormsCompletionRequirementSet(task, taskDetails);
        const enableApprovalCompletionRequirements = config.enableApprovalsRequirement && areApprovalCompletionRequirementSet(task, taskDetails);
        const enableAppPoweredTaskCompletionRequirements = config.enableAPTSection && task.isAppPoweredTask() && !task.isComplete();
        return /*#__PURE__*/ React.createElement("div", {
            ref: this.completionRequirementCalloutRef,
            className: classNames.completeIcon
        }, enableCompletionRequirements || enableFormsCompletionRequirements || enableApprovalCompletionRequirements || enableAppPoweredTaskCompletionRequirements ? /*#__PURE__*/ React.createElement("img", {
            onClick: this.toggleCompletionRequirementsInfoCallout,
            className: "completion-requirements-info-icon",
            src: taskInCompleteIcon,
            title: Strings.TaskEditor_ShowMandatoryChecklistIncompleteLabel
        }) : /*#__PURE__*/ React.createElement(CompleteTaskAnimationButton, {
            id: "completeButton" + task.id,
            isTaskCompleted: isTaskCompleted,
            isFocusZoneEnabled: true,
            isDisabled: !config.permissions.canUpdateProgress,
            completeTaskButtonTabIndex: 0,
            onButtonClick: this.onTaskCompleteClick,
            theme: theme,
            isAITask: isAITask
        }));
    }
    /**
     *
     * Get the priority icon props for the task
     * @param priorityName The priority to get the icon for
     */ getPriorityIconProps(priorityName) {
        return getPriorityIconProps(this.props.theme, priorityName, this.context.traceLogger);
    }
    /**
     * Get the drop down options for the Progress dropdown
     * @param task The task object the editor is using
     */ getProgressList(task) {
        const taskProgressState = this.props.callbacks.convertPercentCompleteToProgressState(task.percentComplete);
        const progressList = [];
        let progressItem;
        progressItem = {
            index: 0,
            selected: taskProgressState === ProgressState.NotStarted,
            key: ProgressState.NotStarted,
            text: Strings.Task_NotStartedText
        };
        progressList.push(progressItem);
        progressItem = {
            index: 1,
            selected: taskProgressState === ProgressState.InProgress,
            key: ProgressState.InProgress,
            text: Strings.Task_InProgressText
        };
        progressList.push(progressItem);
        if (task.canComplete()) {
            progressItem = {
                index: 2,
                selected: taskProgressState === ProgressState.Complete,
                key: ProgressState.Complete,
                text: Strings.Task_CompletedText
            };
            progressList.push(progressItem);
        }
        return progressList;
    }
    /**
     * Get the drop down options for the Bucket dropdown
     * @param dropdownDisabled Flag indicating whether the dropdown is disabled
     */ getBucketList() {
        const { task, planBuckets } = this.props;
        const bucketList = [];
        if (planBuckets == null) {
            // Buckets have not been fetched yet
            return [];
        }
        // Buckets sorting is descending order
        planBuckets.sort((bucket1, bucket2)=>{
            return compareOrderHints(bucket2.orderHint, bucket1.orderHint);
        });
        planBuckets.forEach((bucket, index)=>{
            const dropdownOption = {
                index: index,
                selected: task.bucketId === bucket.id,
                key: bucket.id,
                text: bucket.title
            };
            bucketList.push(dropdownOption);
        });
        return bucketList;
    }
    /**
     * Get the drop down options for the Priority dropdown
     * @param task The task object the editor is using
     */ getPriorityList(task) {
        const { callbacks } = this.props;
        const { permissions } = this.props.config;
        const taskPriorityName = callbacks.convertPriorityToPriorityName(task.priority);
        const priorityList = [];
        let priorityItem;
        if (task.kind !== TaskKind.SharedConsumer) {
            priorityItem = {
                index: 0,
                disabled: !permissions.canUpdatePriorityToUrgent,
                selected: taskPriorityName === PriorityName.Urgent,
                key: PriorityName.Urgent,
                text: Strings.Task_PriorityUrgentText
            };
            priorityList.push(priorityItem);
            priorityItem = {
                index: 1,
                selected: taskPriorityName === PriorityName.Important,
                key: PriorityName.Important,
                text: Strings.Task_PriorityImportantText
            };
            priorityList.push(priorityItem);
            priorityItem = {
                index: 2,
                selected: taskPriorityName === PriorityName.Medium,
                key: PriorityName.Medium,
                text: Strings.Task_PriorityMediumText
            };
            priorityList.push(priorityItem);
            priorityItem = {
                index: 3,
                selected: taskPriorityName === PriorityName.Low,
                key: PriorityName.Low,
                text: Strings.Task_PriorityLowText
            };
            priorityList.push(priorityItem);
        } else {
            priorityItem = {
                index: 0,
                selected: taskPriorityName === PriorityName.Important,
                key: PriorityName.Important,
                text: Strings.Task_PriorityImportantText
            };
            priorityList.push(priorityItem);
            priorityItem = {
                index: 1,
                selected: taskPriorityName === PriorityName.Medium,
                key: PriorityName.Medium,
                text: Strings.Task_PriorityMediumText
            };
            priorityList.push(priorityItem);
        }
        return priorityList;
    }
    /**
     * Get external source view properties for the task
     * @param {IExternalTaskSource} externalSource The external source for the task
     * @returns {IExternalSourceViewProperties} The external source view properties for the task

     */ getTaskExternalSourceViewProperties(externalSource) {
        const { plan } = this.props;
        return {
            titleString: plan?.title ?? "",
            svg: projectSvg,
            buttonText: Strings.ExternalSourceView_Navigate,
            linkColorClass: "projectColor",
            webUrl: externalSource?.webUrl
        };
    }
    /**
     * Render the message banner for project tasks
     */ renderMessageBannerIfProjectTask() {
        const { config, task } = this.props;
        const classNames = this.getClassNames();
        if (config.enableProjectTasksForPlannerWeb && task.isProjectTask()) {
            const externalSourceViewProperties = task.creationSource.external ? this.getTaskExternalSourceViewProperties(task.creationSource.external) : null;
            const url = externalSourceViewProperties?.webUrl;
            const externalContextId = task.creationSource.external?.externalContextId;
            const externalObjectId = task.creationSource.external?.externalObjectId;
            if (externalObjectId == null || externalContextId == null) {
                this.context.traceLogger.logTrace(0x1e3a07d4 /* tag_4o65u */ , TraceLevel.Warning, `renderMessageBannerIfProjectTask called without task external specifications unexpectedly [taskId=${task.id}]`);
            }
            const urlColor = fontColorThemePrimary(this.props.theme)?.color;
            return /*#__PURE__*/ React.createElement("div", {
                className: classNames.messageBanner
            }, /*#__PURE__*/ React.createElement(MessageBar, {
                isMultiline: true,
                messageBarType: MessageBarType.info,
                className: "msgBar"
            }, Strings.TaskEditor_ProjectTaskBannerInfo, url && externalContextId && externalObjectId && /*#__PURE__*/ React.createElement(Link, {
                href: url,
                target: "_blank",
                "aria-label": Strings.ExternalSourceView_LinkText,
                rel: "noopener noreferrer",
                style: {
                    color: urlColor
                },
                onClick: this.props.callbacks.onClickGoToPremiumPlan
            }, Strings.ExternalSourceView_LinkText)));
        } else {
            return null;
        }
    }
    /**
     * Render the plan link
     * @param plan Plan object for the task
     * @param classNames classnames associated with the computed styles
     */ renderPlanLink() {
        const classNames = this.getClassNames();
        const { plan } = this.props;
        const { enablePlanTitleNavLink } = this.props.config;
        const { onNavigateToPlan } = this.props.callbacks;
        if (plan == null) {
            return null;
        }
        if (enablePlanTitleNavLink) {
            return /*#__PURE__*/ React.createElement("div", {
                className: css(classNames.planTitle, "link"),
                tabIndex: 0,
                role: "link",
                onClick: onNavigateToPlan,
                onKeyDown: (e)=>{
                    if (KeyboardUtilities.getKey(e) === KeyCode.ENTER) {
                        onNavigateToPlan();
                        e.preventDefault();
                    }
                }
            }, plan.title);
        } else {
            return /*#__PURE__*/ React.createElement("div", {
                className: classNames.planTitle
            }, plan.title);
        }
    }
    /**
     * Renders a Rooster based Rich Text Editor from PWS.
     */ renderRichTextEditor() {
        const { task, taskDetails, config, theme, scrollContainerRef } = this.props;
        const { permissions } = this.props.config;
        const classNames = this.getClassNames();
        if (taskDetails == null) {
            return null;
        }
        const shouldShowOnCardCheckbox = task.kind === TaskKind.Shared && taskDetails.notes?.content?.length && taskDetails.notes?.content?.length > 0;
        const darkTheme = theme.name === PlannerTheme.HighContrast || theme.name === PlannerTheme.Dark;
        const notesValue = this.state.updatedNotesContent;
        return /*#__PURE__*/ React.createElement("div", {
            className: classNames.description
        }, /*#__PURE__*/ React.createElement("div", {
            className: "descriptionHeader"
        }, /*#__PURE__*/ React.createElement(Label, {
            className: "descriptionTitle"
        }, Strings.TaskEditor_NotesLabel), shouldShowOnCardCheckbox && this.renderShowOnCardCheckbox(1, taskDetails, classNames)), this.state.hasInvalidNotes && /*#__PURE__*/ React.createElement(MessageBar, {
            messageBarType: MessageBarType.error
        }, StringHelper.format(Strings.Notifications_UpdateTaskError, task.title)), /*#__PURE__*/ React.createElement(RichTextEditor, {
            darkTheme: darkTheme,
            value: notesValue ?? undefined,
            keyId: "notes",
            placeholder: Strings.TaskEditor_NotesPlaceholder,
            ariaLabelContent: Strings.TaskEditor_NotesLabel,
            maxLength: config.limits.maxDescriptionLength,
            autoFocus: false,
            isReadOnly: !permissions.canEdit || !permissions.canEditDescription,
            lockContent: false,
            onMiscUserAction: ()=>{},
            onChange: this.onRichTextNotesChanged,
            onFocus: this.onTaskDescriptionFocus,
            onBlur: this.onTaskNotesBlur,
            showLessString: Strings.TaskEditor_NotesShowLess,
            showMoreString: Strings.TaskEditor_NotesShowMore,
            initializeBlockDnDPlugin: true,
            initializeHyperLinkPlugin: true,
            initializeContentEditPlugin: true,
            initializeBlockImagePastePlugin: true,
            initializeEmojiPlugin: true,
            initializeContentLimitPlugin: true,
            initializeUpdateContentPlugin: true,
            initializeWatermarkPlugin: true,
            initializePasteContentPlugin: true,
            previewMaxHeight: NotesPreviewMaxHeight,
            styles: customRichTextEditorStyles(this.props.theme),
            initializeRibbonPlugin: this.props.config.enableRibbon,
            disableCollapseExpanBehavior: this.props.config.disableNotesCollapsingBehavior,
            scrollContainerRef: scrollContainerRef,
            isRoosterV9EditorEnabled: this.props.config.isRoosterV9EditorEnabled
        }));
    }
    /**
     * Render the description section
     * @param classNames classnames associated with the computed styles
     */ renderDescriptionSection() {
        const { task, taskDetails, config } = this.props;
        const { descriptionWithHyperlinks } = this.state;
        const { permissions } = this.props.config;
        const classNames = this.getClassNames();
        if (taskDetails == null) {
            return null;
        }
        const shouldShowOnCardCheckbox = task.kind === TaskKind.Shared && taskDetails && taskDetails.description && taskDetails.description.length > 0;
        const ariaLabel = StringHelper.format(Strings.TaskEditor_NotesAriaLabel, Strings.TaskEditor_NotesLabel, Strings.TaskEditor_NotesPlaceholder);
        return /*#__PURE__*/ React.createElement("div", {
            className: classNames.description
        }, /*#__PURE__*/ React.createElement("div", {
            className: "descriptionHeader"
        }, /*#__PURE__*/ React.createElement(Label, {
            className: "descriptionTitle"
        }, Strings.TaskEditor_NotesLabel), shouldShowOnCardCheckbox && this.renderShowOnCardCheckbox(1, taskDetails, classNames)), this.state.showDescriptionInEditMode || !descriptionWithHyperlinks ? /*#__PURE__*/ React.createElement(TextField, {
            autoAdjustHeight: true,
            title: !permissions.canEditDescription ? Strings.SharedComponents_ReadonlyFieldPermissionsTooltip : undefined,
            className: "edit",
            multiline: true,
            placeholder: Strings.TaskEditor_NotesPlaceholder,
            value: this.state.updatedDescription ?? undefined,
            resizable: true,
            onChange: (event, newValue)=>{
                this.onDescriptionChanged(newValue);
            },
            onKeyDown: this.taskDescriptionKeyPress,
            onFocus: this.onTaskDescriptionFocus,
            onBlur: this.onTaskDescriptionBlur,
            styleType: TextFieldType.Dialog,
            maxLength: config.limits.maxDescriptionLength,
            ariaLabel: ariaLabel,
            disabled: !permissions.canEdit || !permissions.canEditDescription,
            componentRef: (component)=>{
                this.descriptionTextField = component;
            }
        }) : /*#__PURE__*/ React.createElement("div", {
            onFocus: this.onTaskDescriptionFocus,
            tabIndex: 0,
            className: "description-hyperlinks"
        }, descriptionWithHyperlinks));
    }
    renderAppPoweredTaskSection() {
        const { config, task, taskDetails } = this.props;
        if (!config.enableAPTSection || !task.isAppPoweredTask() || taskDetails?.references === undefined || taskDetails?.references && getAppPoweredTaskAttachmentDetails(taskDetails?.references) === null) {
            return null;
        }
        return /*#__PURE__*/ React.createElement(Suspense, {
            fallback: /*#__PURE__*/ React.createElement(React.Fragment, null)
        }, /*#__PURE__*/ React.createElement(LazyAppPoweredTaskSection, {
            references: taskDetails?.references ?? {},
            isTaskComplete: task.isComplete(),
            openAppPoweredTaskInTargetApp: this.props.callbacks.onClickStartAppPoweredTask
        }));
    }
    /**
     * Render the requirements section on task details for adding required forms and approvals
     * Only used in Teams Tasks create publishing task flow
     * @returns JSX element for the requirements section
     */ renderRequirementsSection() {
        const { callbacks, config, task, createdFormId, taskDetails, theme, taskApprovalDetails } = this.props;
        // Todo tasks do not have requirements
        if (task.kind === TaskKind.Todo) {
            return null;
        }
        if (!config.enableFormsRequirement && !config.enableApprovalsRequirement) {
            return null;
        }
        const menuPropsItems = [];
        if (config.enableFormsRequirement) {
            menuPropsItems.push({
                key: "requiredForm",
                text: Strings.TaskEditor_AddRequirementButtonFormOption,
                onClick: ()=>{
                    callbacks.onOpenAddFormRequirementDialog();
                },
                disabled: Boolean(taskDetails?.completionRequirements?.formsRequirement && taskDetails.completionRequirements.formsRequirement.requiredForms.length < config.limits.maxRequiredFormsOnTask)
            });
        }
        if (config.enableApprovalsRequirement) {
            menuPropsItems.push({
                key: "requiredApproval",
                text: Strings.TaskEditor_AddRequirementButtonApprovalOption,
                onClick: ()=>{
                    this.updateApprovalRequirement(true);
                },
                disabled: taskDetails?.completionRequirements?.approvalRequirement?.isApprovalRequired
            });
        }
        const attachmentSectionCallbacks = {
            onDeleteTaskAttachment: this.onDeleteFormAttachment,
            onEditTaskAttachment: this.onEditFormAttachment,
            onSelectDeselectShowOnCard: this.onToggleShowOnCard,
            onOpenLink: this.onOpenFormAttachment,
            onCopyLink: this.onCopyFormAttachmentLink
        };
        const attachmentSectionConfig = {
            disableCopyLink: false,
            disableActionButton: false,
            hideOpenInNewWindow: true,
            hideShowOnCardButton: true,
            disableEdit: !config.permissions.canEdit || isAttachmentPermissionDisabled(config.permissions.attachmentPermissions, AttachmentPermissions.DisableUpdateAttachments) || task.creationSource.publication?.id != null,
            disableRemove: !config.permissions.canEdit || isAttachmentPermissionDisabled(config.permissions.attachmentPermissions, AttachmentPermissions.DisableUpdateAttachments) || task.creationSource.publication?.id != null,
            hideMoreButton: !config.enableAddRequirements,
            disableOpenAttachment: false,
            disableExtraHostnameUrlValidation: config.disableExtraHostnameUrlValidation
        };
        const formAttachmentRowItems = this.getFormAttachmentRowItems(FormUrlType.EditForm);
        const formAttachmentsListProps = {
            callbacks: attachmentSectionCallbacks,
            rowConfig: attachmentSectionConfig,
            taskAttachmentRowItems: formAttachmentRowItems,
            isTaskAttachmentBeingEdited: false
        };
        const isApprovalRequired = taskDetails?.completionRequirements?.approvalRequirement?.isApprovalRequired;
        const approvalInfo = taskDetails?.approval;
        const forms = taskDetails?.forms ? Object.values(taskDetails.forms) : null;
        const callbacksRequirementsSection = {
            onDeleteApprovalRequirement: ()=>this.updateApprovalRequirement(false),
            getAssigneePoolForApproval: callbacks.getAssigneePoolForApproval,
            getUserPhoto: callbacks.getUserPhoto,
            onCancelApproval: callbacks.onCancelApproval,
            onClearApproval: callbacks.onClearApproval,
            onUpdateApproval: callbacks.onUpdateApproval,
            onSendApprovalRequest: callbacks.onSendApprovalRequest,
            onSelectApprover: callbacks.onSelectApprover,
            onReloadApprovalData: callbacks.onReloadApprovalData
        };
        return /*#__PURE__*/ React.createElement(Suspense, {
            fallback: /*#__PURE__*/ React.createElement(React.Fragment, null)
        }, /*#__PURE__*/ React.createElement(LazyRequirementsSection, {
            addRequirementButtonMenuItems: menuPropsItems,
            formAttachmentsListProps: formAttachmentsListProps,
            enableFormsRequirement: config.enableFormsRequirement,
            createdFormId: createdFormId,
            forms: forms,
            formPermissions: config.formPermissions,
            theme: theme,
            enableApprovalsRequirement: config.enableApprovalsRequirement,
            isApprovalRequired: !!isApprovalRequired,
            approvalPermissions: config.approvalPermissions,
            maxRequiredForms: config.limits.maxRequiredFormsOnTask,
            maxRequiredApprovals: config.limits.maxRequiredApprovalsOnTask,
            approvalInfo: approvalInfo,
            approvalDetailInfo: taskApprovalDetails,
            isTaskCompleted: task.isComplete(),
            selectedApprover: this.props.selectedApprover,
            isApprovalDataLoading: this.props.isApprovalDataLoading,
            callbacks: callbacksRequirementsSection,
            userRole: this.props.userRole
        }));
    }
    /**
     * Render the checklist on task details
     * @param classNames classnames associated with the computed styles
     */ renderChecklistSection() {
        const { task, taskDetails, callbacks, config } = this.props;
        const { permissions, limits } = config;
        const checklistItems = taskDetails ? taskDetails.checklist : {};
        const isChecklistRequired = task.specifiedCompletionRequirements & SpecifiedCompletionRequirements.ChecklistItemsCompletion;
        const hasCompletedTaskRequirements = !!(isChecklistRequired && task.isComplete());
        const checklistPermissions = !permissions.canEdit || isChecklistRequired && task.isComplete() ? ChecklistPermissions.DisableAll : permissions.checklistPermissions;
        const classNames = this.getClassNames();
        if (task.isProjectTask()) {
            return null;
        }
        return /*#__PURE__*/ React.createElement("div", {
            className: classNames.checklist
        }, this.renderCompletionRequirementsInfoCallout(classNames), this.renderChecklistPreviewHeader(classNames), /*#__PURE__*/ React.createElement(ChecklistContainer, {
            taskId: task.id,
            checklistItems: checklistItems,
            updateChecklist: this.updateChecklist,
            planId: task.planId,
            bucketId: task.bucketId,
            createTask: callbacks.onCreateTask,
            permissions: checklistPermissions,
            maxChecklistItemsPerTask: limits.maxChecklistItemsPerTask,
            updateCompletionRequirementsInfoCallout: this.toggleCompletionRequirementsInfoCallout,
            showMandatoryChecklistTooltip: hasCompletedTaskRequirements
        }));
    }
    /**
     * Renders the mandatory checklist control with toggle and information section if enabled in config.
     * @param classNames - An object containing class names for styling the control.
     * @returns A JSX element containing the mandatory checklist toggle and information section, or null if toggle is disabled in config.
     */ renderMandatoryChecklistControl(classNames) {
        const { taskDetails } = this.props;
        return /*#__PURE__*/ React.createElement(React.Fragment, null, this.renderMandatoryChecklistToggle(taskDetails, classNames), this.renderMandatoryChecklistInfoSection(classNames));
    }
    /**
     * Render the Checklist fraction summary and the Show on Card option
     * @param classNames classnames associated with the computed styles
     */ renderChecklistPreviewHeader(classNames) {
        const { taskDetails, config, task } = this.props;
        const checkListItems = taskDetails?.checklist || {};
        // Only draw checkbox and progress if we have checklist items
        const shouldDrawFullHeader = find(checkListItems, (checklistItem)=>{
            return checklistItem != null;
        }) != null;
        const itemStats = this.getItemStats(checkListItems);
        const hasChecklistCompletionRequirement = config.enableMandatoryCheckList && !!(task.specifiedCompletionRequirements & SpecifiedCompletionRequirements.ChecklistItemsCompletion);
        let withChecklistRequirementClass = "";
        if (config.enableMandatoryCheckList && hasChecklistCompletionRequirement) {
            withChecklistRequirementClass = task.numberOfActiveChecklistItems === 0 ? classNames.completeTaskRequiredChecklistHeaderItem : classNames.incompleteTaskRequiredChecklistHeaderItem;
        }
        const checklistFraction = StringHelper.format(Strings.TaskEditor_ChecklistFraction, itemStats.numCompletedItems, itemStats.numItems);
        const percentComplete = itemStats.numCompletedItems / itemStats.numItems;
        return /*#__PURE__*/ React.createElement("div", {
            className: "checklistInfo",
            tabIndex: itemStats.numCompletedItems === 0 ? -1 : 0,
            "aria-label": StringHelper.format(Strings.TaskEditor_ChecklistStatus, itemStats.numCompletedItems, itemStats.numItems)
        }, /*#__PURE__*/ React.createElement("div", null, /*#__PURE__*/ React.createElement("label", {
            className: "checklistDetails",
            "aria-label": Strings.TaskEditor_ChecklistLabel
        }, /*#__PURE__*/ React.createElement("span", null, Strings.TaskEditor_ChecklistLabel, hasChecklistCompletionRequirement && /*#__PURE__*/ React.createElement("text", {
            ref: this.checklistAsteriskRef,
            className: classNames.incompleteTaskRequiredChecklistHeaderItem
        }, "*")), hasChecklistCompletionRequirement && task.numberOfActiveChecklistItems !== 0 && /*#__PURE__*/ React.createElement(LabelTag, {
            index: 1,
            isCollapsed: false,
            title: Strings.TaskEditor_ShowMandatoryChecklistToggleLabel,
            showBorder: false
        }), shouldDrawFullHeader && /*#__PURE__*/ React.createElement("span", {
            className: hasChecklistCompletionRequirement ? css(withChecklistRequirementClass, "checklistFraction") : "checklistFraction"
        }, checklistFraction))), /*#__PURE__*/ React.createElement(ProgressIndicator, {
            className: css("checklistCompletionBar", percentComplete === 1 ? "checklistCompleted" : ""),
            percentComplete: percentComplete,
            barHeight: 3,
            styles: {
                progressBar: {
                    transitionDelay: "0s",
                    transitionDuration: "0.2s",
                    transitionProperty: "width",
                    transitionTimingFunction: "ease-in-out",
                    backgroundImage: "linear-gradient(270deg, #00AB54 0%, #094624 100%)"
                }
            },
            ariaLabel: StringHelper.format(Strings.TaskEditor_ChecklistStatus, itemStats.numCompletedItems, itemStats.numItems),
            progressHidden: itemStats.numCompletedItems === 0
        }), /*#__PURE__*/ React.createElement(ChecklistProgressBarConfetti, {
            isLastChecklistItemMarkedComplete: this.state.prevCheckListStats ? this.state.prevCheckListStats.numCompletedItems + 1 === this.state.prevCheckListStats.numItems && itemStats.numCompletedItems === itemStats.numItems : false
        }), this.renderMandatoryChecklistControl(classNames), shouldDrawFullHeader && this.renderShowOnCardCheckbox(0, taskDetails, classNames));
    }
    /**
     * Render the mandatory checklist info
     * @param classNames classnames associated with the computed styles
     * */ renderMandatoryChecklistInfoSection(classNames) {
        const { config, task } = this.props;
        const shouldShowMandatoryChecklistToggle = config.mandatoryChecklistTogglePermissions !== MandatoryChecklistTogglePermissions.HideMandatoryToggle && task.kind !== TaskKind.Todo && task.kind !== TaskKind.SharedConsumer;
        if (!shouldShowMandatoryChecklistToggle) {
            return null;
        }
        return /*#__PURE__*/ React.createElement("div", {
            className: classNames.mandatoryCheckListToggleInfo
        }, /*#__PURE__*/ React.createElement(TooltipHost, {
            directionalHint: DirectionalHint.bottomCenter,
            content: Strings.TaskEditor_ShowMandatoryChecklistToggleInfoDescriptionLabel
        }, /*#__PURE__*/ React.createElement(Icon, {
            ...infoSmallIconProps,
            tabIndex: 0
        })));
    }
    /**
     * Render a show on card checkbox and setup the callbacks correctly
     * @param taskDetails The TaskDetails associated with this edit dialog
     * @param classNames classnames associated with the computed styles (used for styling the checkbox) and the computed styles     */ renderMandatoryChecklistToggle(taskDetails, classNames) {
        const { task, config } = this.props;
        const mandatoryChecklistToggleEnabled = config.mandatoryChecklistTogglePermissions !== MandatoryChecklistTogglePermissions.HideMandatoryToggle;
        const shouldDisableMandatoryChecklistToggle = config.mandatoryChecklistTogglePermissions === MandatoryChecklistTogglePermissions.DisableMandatoryToggle;
        const shouldShowMandatoryChecklistToggle = mandatoryChecklistToggleEnabled && task.kind !== TaskKind.Todo && task.kind !== TaskKind.SharedConsumer && taskDetails;
        if (!shouldShowMandatoryChecklistToggle) {
            return null;
        }
        return /*#__PURE__*/ React.createElement("div", {
            className: classNames.setMandatoryChecklistToggle
        }, /*#__PURE__*/ React.createElement(Toggle, {
            className: this.state.isChecklistMandatory ? css("ghostTasksToggle", "toggleOn") : css("ghostTasksToggle", "toggleOff"),
            checked: this.state.isChecklistMandatory,
            label: Strings.TaskEditor_ShowMandatoryChecklistToggleLabel,
            onChange: this.onMandatoryChecklistToggleChange,
            ariaLabel: Strings.TaskEditor_ShowMandatoryChecklistToggleLabel,
            disabled: shouldDisableMandatoryChecklistToggle
        }));
    }
    /**
     * Render a show on card checkbox and setup the callbacks correctly
     * @param showOnCardType The (section) type this checkbox is associated with
     * @param taskDetails The TaskDetails associated with this edit dialog
     * @param classNames classnames associated with the computed styles
     */ renderShowOnCardCheckbox(showOnCardType, taskDetails, classNames) {
        const { task, config, theme } = this.props;
        if (task.kind !== TaskKind.Shared) {
            return null;
        }
        if (taskDetails == null) {
            return null;
        }
        let isChecked = false;
        // When fabric fixes the checkbox aria label functionality, use this variable VSO #1795695
        let ariaLabel = Strings.TaskEditor_ShowOnCardLabel;
        if (showOnCardType === 0) {
            isChecked = taskDetails.previewType === PreviewType.CheckList;
            ariaLabel = StringHelper.format(Strings.TaskEditor_ShowOnCardAriaLabel, Strings.TaskEditor_ChecklistLabel);
        } else if (showOnCardType === 1) {
            isChecked = taskDetails.previewType === PreviewType.Description;
            ariaLabel = StringHelper.format(Strings.TaskEditor_ShowOnCardAriaLabel, Strings.TaskEditor_NotesLabel);
        }
        return /*#__PURE__*/ React.createElement("div", {
            className: classNames.setPreviewCheckbox
        }, /*#__PURE__*/ React.createElement(Checkbox, {
            checked: isChecked,
            label: Strings.TaskEditor_ShowOnCardLabel,
            onChange: this.onPreviewTypeChanged.bind(this, showOnCardType),
            ariaLabel: ariaLabel,
            disabled: !config.permissions.canUpdatePreviewType,
            appTheme: theme
        }));
    }
    /**
     * Triggered when a ShowOnCard checkbox was changed
     * @param showOnCardType What type of checkbox was changed
     * @param [event] The event that triggered a change
     * @param [isChecked] Whether or not the checkbox was checked
     */ onPreviewTypeChanged(showOnCardType, event, isChecked) {
        const { taskDetails: currentTaskDetails, callbacks } = this.props;
        if (currentTaskDetails == null) {
            // This should never happen but adding this to satisfy TS
            return;
        }
        let updatedTaskDetails;
        if (!isChecked) {
            updatedTaskDetails = currentTaskDetails.setProperty("previewType", PreviewType.NoPreview);
        } else {
            if (showOnCardType === 0) {
                updatedTaskDetails = currentTaskDetails.setProperty("previewType", PreviewType.CheckList);
            } else if (showOnCardType === 1) {
                updatedTaskDetails = currentTaskDetails.setProperty("previewType", PreviewType.Description);
            }
        }
        if (updatedTaskDetails) {
            callbacks.onUpdateTaskDetails(currentTaskDetails, updatedTaskDetails);
        }
    }
    /**
     * Render section showing who last modified or completed the task
     * @param task The task
     * @param classNames classnames associated with the computed styles
     */ renderLastModifiedSection() {
        const { task } = this.props;
        const { callbacks, config, currentUser } = this.props;
        const classNames = this.getClassNames();
        if (!config.enableLastModifiedBy) {
            return null;
        }
        if (task.isComplete() && task.completedBy != null && task.completedDate != null) {
            const user = callbacks.getUserById(task.completedBy);
            if (user == null) {
                return null;
            }
            const userName = user.formattedDisplayName;
            const stringResource = user.id === currentUser.id ? Strings.TaskEditor_CompletedByOnDateByYou : Strings.TaskEditor_CompletedByOnDate;
            // If the task is a todo, we want to show the date directly due to todo tasks only having a completion date
            // Reference moment format doc https://momentjscom.readthedocs.io/en/latest/moment/04-displaying/01-format/#:~:text=Month%20numeral%2C%20day%20of%20month%2C%20year
            const formattedDate = task.kind === TaskKind.Todo ? task.completedDate.format("L") : this.getElapsedTimeApproximation(task.completedDate);
            const formattedString = user.id === currentUser.id ? StringHelper.format(stringResource, formattedDate) : StringHelper.format(stringResource, formattedDate, userName);
            return /*#__PURE__*/ React.createElement("div", {
                className: css(classNames.lastModifiedSection, "completed")
            }, formattedString);
        } else if (task.userContentLastModifiedBy != null && task.userContentLastModifiedDate != null) {
            const user = callbacks.getUserById(task.userContentLastModifiedBy);
            if (user == null) {
                return null;
            }
            const userName = user.formattedDisplayName;
            const stringResource = user.id === currentUser.id ? Strings.TaskEditor_ModifiedByOnDateByYou : Strings.TaskEditor_ModifiedByOnDate;
            const formattedDate = this.getElapsedTimeApproximation(task.userContentLastModifiedDate);
            const formattedString = user.id === currentUser.id ? StringHelper.format(stringResource, formattedDate) : StringHelper.format(stringResource, formattedDate, userName);
            return /*#__PURE__*/ React.createElement("div", {
                className: classNames.lastModifiedSection
            }, formattedString);
        }
        return null;
    }
    /**
     * Get the number of completed checklist items and number of total checklist items
     * @param checkListItems Items on checklist
     */ getItemStats(checklistItems) {
        const noNullValues = compact(values(checklistItems));
        const numItems = noNullValues.length;
        const numCompletedItems = noNullValues.filter((item)=>{
            return item.isChecked;
        }).length;
        return {
            numItems: numItems,
            numCompletedItems: numCompletedItems
        };
    }
    /**
     * Render the attachments on task details
     */ renderAttachments() {
        const { attachmentToEdit, callbacks, config, container, task } = this.props;
        const attachmentRowItems = this.getAttachmentRowItems();
        if (task.kind === TaskKind.SharedConsumer) {
            // Shared consumer tasks do not support attachments
            return null;
        }
        if (isAttachmentPermissionDisabled(config.permissions.attachmentPermissions, AttachmentPermissions.DisableAll) && attachmentRowItems.length === 0) {
            // If attachments are fully disabled and there are no attachments, do not render the attachments section
            return null;
        }
        let driveUrl;
        if (container?.containerType === ContainerType.Group) {
            const groupContainer = container;
            driveUrl = groupContainer.unifiedGroupDetails?.getWorkloadUrl(GroupWorkLoad[GroupWorkLoad.Documents]);
        }
        const attachmentSectionConfig = {
            fileUploadOption: this.getFileUploadOption(),
            sharepointOption: !!container && !!driveUrl ? ControlRenderMode.Default : ControlRenderMode.Hidden,
            hideOpenInNewWindow: !config.enableOpenInNewWindow,
            hideShowOnCardButton: task.kind !== TaskKind.Shared,
            disableEditAttachment: !config.permissions.canEdit || isAttachmentPermissionDisabled(config.permissions.attachmentPermissions, AttachmentPermissions.DisableUpdateAttachments) || task.creationSource.publication?.id != null,
            disableRemoveAttachment: !config.permissions.canEdit || isAttachmentPermissionDisabled(config.permissions.attachmentPermissions, AttachmentPermissions.DisableUpdateAttachments) || task.creationSource.publication?.id != null,
            addAttachmentButtonOption: this.getAddAttachmentButtonOption(config.permissions, task.kind),
            disableCopyLink: true,
            disableActionButton: true,
            hideMoreButton: false,
            disableOpenAttachment: false,
            disableExtraHostnameUrlValidation: config.disableExtraHostnameUrlValidation
        };
        const attachmentSectionCallbacks = {
            onDeleteTaskAttachment: this.deleteAttachment,
            onOpenAddLinkDialog: this.openAddLinkDialog,
            onOpenTaskAttachmentEditorDialog: this.onOpenAttachmentEditorDialog,
            onOpenOneDrivePicker: callbacks.onOpenOnedrivePicker,
            onOpenErrorDialog: callbacks.onOpenErrorDialog,
            onUploadFileAsTaskAttachment: this.onUploadFileAsAttachment,
            onSelectDeselectShowOnCard: this.onToggleShowOnCard,
            onOpenLink: this.onOpenLink,
            onSelectEmptyFile: callbacks.onSelectEmptyFile,
            onCopyLink: ()=>{}
        };
        let attachmentRowToEdit;
        if (attachmentToEdit) {
            const documentPreviewThumbnail = callbacks.getAttachmentPreviewThumbnail(attachmentToEdit.url);
            const attachmentSelectedForPreviewUrl = this.getAttachmentSelectedForPreviewUrl();
            attachmentRowToEdit = TaskAttachmentRowItem.builder.fromTaskAttachment(attachmentToEdit, documentPreviewThumbnail || "").withPropertyBag({
                isShownInTaskCard: attachmentSelectedForPreviewUrl === attachmentToEdit.url
            }).build();
        }
        const customStyles = customAttachmentSectionStyles(this.props.theme);
        return /*#__PURE__*/ React.createElement(AttachmentsSection, {
            attachmentSectionConfig: attachmentSectionConfig,
            maximumAttachmentsPerTask: config.limits.maximumAttachments,
            attachmentRowItems: attachmentRowItems,
            attachmentSectionCallbacks: attachmentSectionCallbacks,
            attachmentToEdit: attachmentRowToEdit,
            styles: customStyles
        });
    }
    /**
     * Method to get the attachment row items: this ones can be upload sessions or attachments.
     */ getAttachmentRowItems() {
        const { taskDetails, callbacks, uploadPendingAttachments } = this.props;
        if (taskDetails == null || taskDetails.references == null) {
            return [];
        }
        const attachments = taskDetails.getAttachmentsSortedByAlias();
        attachments.push(...uploadPendingAttachments);
        const taskAttachmentRowItems = [];
        const attachmentSelectedForPreviewUrl = this.getAttachmentSelectedForPreviewUrl();
        forEach(attachments, (attachment)=>{
            // We do not count app powered reference as attachment
            if (attachment.taskAttachmentType.taskAttachmentType === SupportedReferenceTypes.TeamsHostedApp) {
                return null;
            }
            if (!LocalIdGenerator.isLocalTempId(attachment.url)) {
                const documentPreviewThumbnail = callbacks.getAttachmentPreviewThumbnail(attachment.url);
                taskAttachmentRowItems.push(TaskAttachmentRowItem.builder.fromTaskAttachment(attachment, documentPreviewThumbnail || "").withPropertyBag({
                    isShownInTaskCard: attachmentSelectedForPreviewUrl === attachment.url
                }).build());
            } else {
                const driveItemUploadSession = callbacks.getDocumentUploadSession(attachment.url);
                driveItemUploadSession && taskAttachmentRowItems.push(TaskAttachmentRowItem.builder.fromDriveItemUploadSession(driveItemUploadSession).build());
            }
        });
        return taskAttachmentRowItems;
    }
    /**
     * Method to get the form attachment row items from forms on task details
     * @param formUrlType The type of form url to use for the attachment url
     */ getFormAttachmentRowItems(formUrlType) {
        const { taskDetails } = this.props;
        if (taskDetails == null || taskDetails.forms == null) {
            return [];
        }
        const forms = taskDetails.getFormsSortedByAlias();
        const taskAttachmentRowItems = [];
        forEach(forms, (form)=>{
            taskAttachmentRowItems.push(TaskAttachmentRowItem.builder.fromForm(form, formUrlType).build());
        });
        return taskAttachmentRowItems;
    }
    /**
     * Get the Url of attachment which is selected for preview
     */ getAttachmentSelectedForPreviewUrl() {
        const { taskDetails } = this.props;
        if (!taskDetails) {
            return "";
        }
        const lowestPriorityReference = taskDetails.getReferenceWithLowestPriority();
        const attachmentSelectedForPreviewUrl = taskDetails.previewType === PreviewType.Reference || taskDetails.previewType === PreviewType.Automatic ? lowestPriorityReference ? lowestPriorityReference.url : undefined : undefined;
        return attachmentSelectedForPreviewUrl || "";
    }
    /**
     * Get the file upload option for the attachment section config
     */ getFileUploadOption() {
        const { container, config } = this.props;
        // Short circuit: no container means hidden
        if (!container) {
            return ControlRenderMode.Hidden;
        }
        // Teams channel specific logic
        if (config.enablePlanInTeamsChannel && container.containerType === ContainerType.TeamsChannel) {
            return config.enableFileUpload ? ControlRenderMode.Default : ControlRenderMode.Hidden;
        }
        // Group container logic
        let driveUrl;
        if (container.containerType === ContainerType.Group) {
            const groupContainer = container;
            driveUrl = groupContainer.unifiedGroupDetails?.getWorkloadUrl(GroupWorkLoad[GroupWorkLoad.Documents]);
        }
        return config.enableFileUpload && !!driveUrl ? ControlRenderMode.Default : ControlRenderMode.Hidden;
    }
    /**
     * Get the attachment given a task attachment row item
     * @param attachmentRowItem the task attachment row item
     */ getAttachmentFromTaskAttachmentRowItem(attachmentRowItem) {
        // Get the attachment from the row item
        const attachments = this.props.taskDetails?.getAttachmentsSortedByAlias();
        return find(attachments, (attachment)=>{
            return attachment.url === attachmentRowItem.url;
        });
    }
    /**
     * Get the form attachment given a task attachment row item
     * @param { ITaskAttachmentRowItem } attachmentRowItem The task attachment row item
     * @param { FormUrlType } urlType The Form URL Type
     * @returns { IAttachment | undefined } The Form Attachment
     */ getFormAttachmentFromTaskAttachmentRowItem(attachmentRowItem, urlType) {
        const forms = this.props.taskDetails?.getFormsSortedByAlias();
        const form = find(forms, (form)=>{
            return form.formId === attachmentRowItem.id;
        });
        return form ? Attachment.builder.attachmentFromForm(form, urlType).build() : undefined;
    }
    /**
     * Get the form given a task attachment row item
     * @param attachmentRowItem the task attachment row item
     */ getFormFromTaskAttachmentRowItem(attachmentRowItem) {
        const forms = this.props.taskDetails?.getFormsSortedByAlias();
        const form = find(forms, (form)=>{
            return form.formId === attachmentRowItem.id;
        });
        return form;
    }
    /**
     * Callback for updating the state with the freshly typed title from the title box
     * @param title The latest title value
     */ onTitleChanged(title) {
        this.setState((prevState)=>{
            return {
                updatedTitle: title
            };
        });
    }
    /**
     * Update the state with freshly typed description
     * @param description New description
     */ onDescriptionChanged(descriptionField) {
        this.throttledTaskDescriptionUpdate(descriptionField);
        // Update the state to latest description after throttledTaskDescriptionUpdate to always have correct state because, throttledTaskDescriptionUpdate would dispatch an updateTask action
        // which would result in a setState call with the current state on dispatch action. Since setState is an async operation, if we call it before throttledTaskDescriptionUpdate call,
        // we might not have latest state at that point and this will result state getting reset to old description.
        this.setState((prevState)=>{
            return {
                updatedDescription: descriptionField,
                descriptionWithHyperlinks: convertUrlsToRenderableLinks(descriptionField)
            };
        });
    }
    /**
     * What to call when task description edit is completed
     * @param newDescription New task description
     */ taskDescriptionUpdate(newDescription) {
        const { taskDetails: currentTaskDetails, callbacks, config } = this.props;
        const { traceLogger } = this.context;
        if (currentTaskDetails == null) {
            return;
        }
        if (!config.disableTaskNotesEscapeCharacterValidation) {
            // Don't save notes that contain escape characters - these are likely mis-encoded and will be illegible (e.g. ISO-2022-JP instead of UTF-8)
            const hasEscapeChar = newDescription.includes("\x1B");
            if (hasEscapeChar !== this.state.hasInvalidNotes) {
                this.setState({
                    hasInvalidNotes: hasEscapeChar
                });
            }
            if (hasEscapeChar) {
                traceLogger.logTrace(0x1e10d08f /* tag_4encp */ , TraceLevel.Error, "Task description update blocked due to escape character");
                return;
            }
        }
        const updatedTaskDetails = config.enableRichTextEditor ? currentTaskDetails.setProperty("notes", {
            content: newDescription.trim(),
            contentType: ContentType.HTML
        }) : currentTaskDetails.setProperty("description", newDescription.trim());
        callbacks.onUpdateTaskDetails(currentTaskDetails, updatedTaskDetails);
    }
    /**
     * What to call when task description is done being edited and a change has occurred
     */ alertUserDescriptionUpdated() {
        const { callbacks } = this.props;
        callbacks.onAlertUserDescriptionUpdated();
        this.descriptionSinceLastUpdateAlert = this.props.config.enableRichTextEditor ? this.state.updatedNotesContent : this.state.updatedDescription;
    }
    /**
     * Compare a lastModifiedTimestamp to the current time and return a string indicating about how much time has passed since the task was last modified.
     * @param lastModifiedTimestamp the lastModifiedTimestamp to compare
     */ getElapsedTimeApproximation(lastModifiedTimestamp) {
        const now = moment();
        const duration = moment.duration(now.diff(lastModifiedTimestamp));
        if (InLastMinute(lastModifiedTimestamp)) {
            return Strings.TaskEditor_LastModifiedMomentsAgo;
        } else if (InPastFewMinutes(lastModifiedTimestamp)) {
            return StringHelper.format(Strings.TaskEditor_LastModifiedMinutesAgo, duration.minutes());
        } else if (InPastHour(lastModifiedTimestamp)) {
            return Strings.TaskEditor_LastModifiedHourAgo;
        } else if (InPastFewHours(lastModifiedTimestamp)) {
            return StringHelper.format(Strings.TaskEditor_LastModifiedHoursAgo, duration.hours());
        } else if (IsToday(lastModifiedTimestamp)) {
            return Strings.TaskEditor_LastModifiedToday;
        } else if (IsYesterday(lastModifiedTimestamp)) {
            return Strings.TaskEditor_LastModifiedYesterday;
        } else if (InPastFewDays(lastModifiedTimestamp)) {
            return StringHelper.format(Strings.TaskEditor_LastModifiedDaysAgo, duration.days());
        } else {
            return lastModifiedTimestamp.format("L");
        }
    }
    /**
     * Logs TaskEditor open if the access context is user. Contains the plan's displayAs info if it exists
     * @param plan The plan for the task
     * @param planDetails The planDetails for the task
     */ logUserAccessEditorOpen(plan, planDetails) {
        const { taskAccessContext } = this.props;
        if (taskAccessContext === TaskAccessContext.AssignedToUser || taskAccessContext === TaskAccessContext.MyDay) {
            const contextDisplayAs = PlanEntitySet.getFirstDisplayableContextString(plan, planDetails);
            this.logUsage(taskAccessContext === TaskAccessContext.AssignedToUser ? "MyTasksOpenTaskEditor" : "MyDayOpenTaskEditor", {
                displayAs: contextDisplayAs
            });
        }
        this.logFloodgateActivity();
    }
    /**
     * Signal floodgate engine to show pre-configured campaign on demand
     */ logFloodgateActivity() {
    // TODO: Add floodgate logging.
    }
    /**
     * Callback to render the task recurrence dropdown
     * PBI 5070063 - Implement task editor UI updates
     * @param classNames classnames associated with the computed styles
     */ renderTaskRecurrenceDropdown(classNames) {
        if (!this.props.config.enableRecurringTasks) {
            return null;
        }
        const { permissions } = this.props.config;
        return /*#__PURE__*/ React.createElement("div", {
            className: "dropdownTuple"
        }, /*#__PURE__*/ React.createElement(DebouncedDropdown, {
            className: "taskRecurrence",
            title: !permissions.canEdit ? Strings.SharedComponents_ReadonlyFieldPermissionsTooltip : undefined,
            label: Strings.TaskRecurrence_TaskRecurrenceLabel,
            options: this.recurrenceOptionList,
            onChange: this.onRecurrenceChanged,
            selectedKey: this.getRecurrenceDropdownType(),
            onRenderTitle: (options)=>{
                const option = options[0];
                return /*#__PURE__*/ React.createElement("div", {
                    className: css("taskEditor_taskRecurrenceItem", classNames.dropdownsItems)
                }, /*#__PURE__*/ React.createElement("div", null, /*#__PURE__*/ React.createElement("div", {
                    className: "icon"
                }, /*#__PURE__*/ React.createElement(Icon, recurrenceIconProps)), /*#__PURE__*/ React.createElement("div", {
                    className: "textValue"
                }, option.text)));
            },
            onRenderOption: (option)=>{
                return /*#__PURE__*/ React.createElement("div", {
                    className: css("taskEditor_taskRecurrenceItem", classNames.bucketRecurrenceDropdown)
                }, /*#__PURE__*/ React.createElement("div", null, option.text));
            },
            disabled: !permissions.canEdit || !permissions.canUpdateRecurrence || this.props.task.recurrence?.isReadonly() || this.props.task.isComplete(),
            debounceInterval: TaskEditor.debounceDropdownTaskUpdateInterval
        }));
    }
    /**
     * Get recurrence dropdown type, returns Custom if interval > 1.
     */ getRecurrenceDropdownType() {
        const pattern = this.props.task.recurrence?.schedule?.pattern;
        if (pattern == null) {
            return RecurrenceDropdownType.NotRepeat;
        }
        if (pattern.interval > 1) {
            return RecurrenceDropdownType.Custom;
        }
        switch(pattern.recurrencePatternType){
            case RecurrencePatternType.Daily:
                return RecurrenceDropdownType.Daily;
            case RecurrencePatternType.Weekly:
                const daysOfWeek = pattern.daysOfWeek;
                // weekdays if length of daysOfWeek is 5 and not including saturday and sunday
                return daysOfWeek.length === 5 && daysOfWeek.indexOf(DayOfWeek.Saturday) < 0 && daysOfWeek.indexOf(DayOfWeek.Sunday) < 0 ? RecurrenceDropdownType.Weekdays : RecurrenceDropdownType.Weekly;
            case RecurrencePatternType.AbsoluteMonthly:
            case RecurrencePatternType.RelativeMonthly:
                return RecurrenceDropdownType.Monthly;
            case RecurrencePatternType.AbsoluteYearly:
            case RecurrencePatternType.RelativeYearly:
                return RecurrenceDropdownType.Yearly;
            default:
                return RecurrenceDropdownType.NotRepeat;
        }
    }
    /**
     * Get the classnames associated with the computed styles
     */ getClassNames() {
        const { theme, config, task } = this.props;
        return getClassNames(getStyles(theme, config.permissions, task.isComplete(), this.props.styles));
    }
    /**
     * Initializes an instance of the Task details editor
     */ constructor(props, context){
        super(props, context), /** Reference to notes  completionRequirementCallout component */ this.completionRequirementCalloutRef = /*#__PURE__*/ React.createRef(), /** Reference to AssignmentControl Component */ this.assignmentControlRef = /*#__PURE__*/ React.createRef(), /** Reference to notes completionRequirementCallout component when user clicks on incomplete checklist */ this.checklistAsteriskRef = /*#__PURE__*/ React.createRef(), /**
     * Callback to update plan details with new category text
     * @param { string[] } newLabelNames Updated label names
     */ this.onChangeLabelName = (newLabelNames)=>{
            if (this.props.planDetails == null) {
                return;
            }
            const updatedPlanDetails = this.props.planDetails.setProperty("categories", newLabelNames);
            this.props.callbacks.onUpdatePlanDetails(this.props.planDetails, updatedPlanDetails);
        }, /**
     * Callback to change the selected categories on the task
     * @param { number[] } selectedIndices Indexes of selected categories.
     */ this.onChangeCategorySelection = (selectedIndices)=>{
            const { engagementLogger } = this.context;
            engagementLogger.logEngagement(this.name(), EngagementTelemetryConstants.TaskLabelsChanged);
            const updatedTask = this.props.task.setProperty("appliedCategories", selectedIndices);
            this.props.callbacks.onUpdateTask(this.props.task, updatedTask);
        }, /**
     * Called when the complete button is clicked
     * @param event Simple user interface events
     */ this.onTaskCompleteClick = (event)=>{
            const { task } = this.props;
            const { onUpdateTask } = this.props.callbacks;
            const { engagementLogger } = this.context;
            // Must be done in a setTimeout so that setState changes before this is fired.
            setTimeout(()=>{
                if (task != null) {
                    const updatedTask = task.toggleCompletionState();
                    onUpdateTask(task, updatedTask);
                }
            }, 0);
            if (task.isComplete()) {
                engagementLogger.logEngagement(this.name(), EngagementTelemetryConstants.TaskReactivateButtonClicked, {
                    taskCreationSourceType: task.taskCreationSourceType ?? ""
                });
            } else {
                engagementLogger.logEngagement(this.name(), EngagementTelemetryConstants.TaskCompleteButtonClicked, {
                    taskCreationSourceType: task.taskCreationSourceType ?? ""
                });
            }
        }, /**
     * Add or remove approval requirement from the task details
     * @param isApprovalRequired if approval is required or not
     */ this.updateApprovalRequirement = (isApprovalRequired)=>{
            const { taskDetails, callbacks } = this.props;
            if (taskDetails == null) {
                return;
            }
            // Update approval requirement if it is different from the current value
            const isApprovalCurrentlyRequired = taskDetails.completionRequirements?.approvalRequirement?.isApprovalRequired;
            if (isApprovalCurrentlyRequired !== isApprovalRequired) {
                const updatedTaskDetails = taskDetails.updateApprovalRequirement(isApprovalRequired);
                callbacks.onUpdateTaskDetails(taskDetails, updatedTaskDetails);
            }
        }, /**
     * A method that sets the completion requirements for a task using the provided ICompletionRequirements object,
     * or null if the requirements are not specified. It returns an instance of TaskDetailsBuilder.
     * @param requirement An object of type ICompletionRequirements that contains the completion requirements for the task,
     * or null if they are not specified.
     */ this.onMandatoryChecklistToggleChange = throttle((event, isChecked)=>{
            const { taskDetails: currentTaskDetails, callbacks } = this.props;
            const listOfchecklistIds = Object.keys(currentTaskDetails?.checklist || {});
            if (currentTaskDetails == null) {
                // This should never happen but adding this to satisfy TS
                return;
            }
            let updatedTaskDetails;
            if (!isChecked || listOfchecklistIds.length === 0) {
                updatedTaskDetails = currentTaskDetails.setProperty("completionRequirements", null);
            } else {
                const checklistRequirement = new ChecklistCompletionRequirement(listOfchecklistIds);
                const completionRequirements = CompletionRequirements.builder.withChecklistRequirement(checklistRequirement).build();
                updatedTaskDetails = currentTaskDetails.setProperty("completionRequirements", completionRequirements);
            }
            this.setState((prevState)=>{
                return {
                    isChecklistMandatory: !prevState.isChecklistMandatory
                };
            });
            // On update TaskDetails, we need to update the taskDetails in the state
            callbacks.onUpdateTaskDetails(currentTaskDetails, updatedTaskDetails);
        }, 500), /**
     *
     * @param ITaskPermissions permissions
     * @param TaskKind taskKind
     * @returns ControlRenderMode for Add Attachment button based on permissions
     */ this.getAddAttachmentButtonOption = (permissions, taskKind)=>{
            if (isAttachmentPermissionDisabled(permissions.attachmentPermissions, AttachmentPermissions.DisableAddAttachment) && taskKind === TaskKind.Todo) {
                return ControlRenderMode.Hidden;
            }
            if (!permissions.canEdit || isAttachmentPermissionDisabled(permissions.attachmentPermissions, AttachmentPermissions.DisableUpdateAttachments) || isAttachmentPermissionDisabled(permissions.attachmentPermissions, AttachmentPermissions.DisableAddAttachment)) {
                return ControlRenderMode.Disabled;
            }
            return ControlRenderMode.Default;
        }, /**
     * Set the preview type to Reference and change the preview priority to set the new prioritized attachment
     * @param { IAttachment } attachment Attachment we want to use for preview
     * @param { boolean } setAsPreview Whether to set the attachment as preview or not
     */ this.setAttachmentPreviewStatus = (attachment, setAsPreview)=>{
            const { taskDetails: currentTaskDetails, callbacks } = this.props;
            if (currentTaskDetails == null) {
                return;
            }
            let updatedTaskDetails;
            const currentPreviewAttachment = currentTaskDetails.getReferenceWithLowestPriority();
            if (currentPreviewAttachment != null && currentPreviewAttachment.url !== attachment.url || currentTaskDetails.previewType !== PreviewType.Reference) {
                updatedTaskDetails = currentTaskDetails.setProperty("previewType", PreviewType.Reference);
                const updatedAttachment = attachment.setProperty("previewPriority", generateOrderHintBetween(null, currentPreviewAttachment == null ? null : currentPreviewAttachment.previewPriority));
                updatedTaskDetails = updatedTaskDetails.replaceAttachment(attachment, updatedAttachment);
            } else {
                // If the user selected the current lowest and the current preview type is reference, then we're deselecting and should reset the preview type
                if (currentTaskDetails.previewType === PreviewType.Reference) {
                    updatedTaskDetails = currentTaskDetails.setProperty("previewType", PreviewType.NoPreview);
                }
            }
            if (updatedTaskDetails) {
                callbacks.onUpdateTaskDetails(currentTaskDetails, updatedTaskDetails);
            }
        }, /**
     * Callback for when a file is uploaded and attached to a task
     * @param { File} file - File which is uploaded
     */ this.onUploadFileAsAttachment = (file)=>{
            const { taskDetails, callbacks, container } = this.props;
            if (taskDetails == null) {
                return;
            }
            if (container == null || container.containerType !== ContainerType.Group && container.containerType !== ContainerType.TeamsChannel) {
                return;
            }
            // Place new attachment at end of list, or highest priority number
            let highestPriority = null;
            if (size(taskDetails.references) > 0) {
                const highPriRef = taskDetails.getReferenceWithHighestPriority();
                highestPriority = highPriRef ? highPriRef.previewPriority : null;
            }
            const newPriority = generateOrderHintBetween(highestPriority, null);
            callbacks.onUploadFileAsAttachment(file, taskDetails, container, newPriority);
        }, /**
     * Delegate to open the add link dialog
     * @param elementToFocusOnDismiss Element to focus on dismiss
     */ this.openAddLinkDialog = (elementToFocusOnDismiss)=>{
            this.props.callbacks.onOpenAttachmentEditorDialog(null, elementToFocusOnDismiss);
        }, /**
     * Callback for removing an attachment from the task
     * @param attachment Attachment to delete
     */ this.deleteAttachment = (attachmentRowItem)=>{
            const { taskDetails: currentTaskDetails, callbacks } = this.props;
            if (currentTaskDetails == null) {
                return;
            }
            const referenceWithLowestPriority = currentTaskDetails.getReferenceWithLowestPriority();
            if (referenceWithLowestPriority == null) {
                // This means that there is no attachment on the details
                return;
            }
            let updatedTaskDetails = currentTaskDetails.removeAttachment(attachmentRowItem.url);
            this.logUsage(EngagementTelemetryConstants.DeleteAttachment, {
                entityId: currentTaskDetails.id
            });
            // If the attachment we're deleting is the current attachment used for preview, we reset the preview type to NoPreview.
            const currentPreviewUrl = indexOf([
                PreviewType.Reference,
                PreviewType.Automatic
            ], currentTaskDetails.previewType) >= 0 ? referenceWithLowestPriority.url : null;
            if (attachmentRowItem.url === currentPreviewUrl) {
                updatedTaskDetails = updatedTaskDetails.setProperty("previewType", PreviewType.NoPreview);
            }
            // Now update the task details to reflect the changes
            callbacks.onUpdateTaskDetails(currentTaskDetails, updatedTaskDetails);
        }, /**
     * Open the attachment editor for given attachment
     * @param attachmentRowItem Attachment row item to be edited
     */ this.onOpenAttachmentEditorDialog = (attachmentRowItem)=>{
            const attachmentToEdit = this.getAttachmentFromTaskAttachmentRowItem(attachmentRowItem);
            attachmentToEdit && this.props.callbacks.onOpenAttachmentEditorDialog(attachmentToEdit);
        }, /**
     * Select or deselect an attachment for preview
     * @param attachmentRowItem Attachment row item to edit
     * @param isSelected Select or deselect this attachment for preview
     */ this.onToggleShowOnCard = (attachmentRowItem, isSelected)=>{
            const attachmentToSetForPreview = this.getAttachmentFromTaskAttachmentRowItem(attachmentRowItem);
            attachmentToSetForPreview && this.setAttachmentPreviewStatus(attachmentToSetForPreview, isSelected);
        }, /**
     * Open the link for given attachment
     * @param attachmentRowItem Attachment row item to open link for
     */ this.onOpenLink = (attachmentRowItem)=>{
            if (!attachmentRowItem.isBeingUploaded) {
                const attachmentToOpen = this.getAttachmentFromTaskAttachmentRowItem(attachmentRowItem);
                attachmentToOpen && this.props.callbacks.onOpenLink(attachmentToOpen);
            }
        }, /**
     * Open the form for the given form attachment
     * @param attachmentRowItem Attachment row item to open link for
     */ this.onOpenFormAttachment = (attachmentRowItem)=>{
            const { callbacks, config } = this.props;
            if (!attachmentRowItem.isBeingUploaded) {
                // If we are in publishing flow, clicking form attachment should open the form url in the browser
                if (config.enableAddRequirements) {
                    const formAttachment = this.getFormAttachmentFromTaskAttachmentRowItem(attachmentRowItem, FormUrlType.EditForm);
                    formAttachment && callbacks.onOpenLink(formAttachment);
                } else {
                    const form = this.getFormFromTaskAttachmentRowItem(attachmentRowItem);
                    if (!form) return;
                    // Open the form response view dialog if there is a form response, otherwise open the iframe dialog to submit a response
                    if (form.formResponse) {
                        callbacks.onOpenFormResponseViewDialog(form);
                    } else {
                        callbacks.onOpenIframeFormRequirementDialog(form);
                    }
                }
            }
        }, /**
     * Callback for removing a form from the task
     * @param attachmentRowItem form attachment to delete
     */ this.onEditFormAttachment = (attachmentRowItem)=>{
            const { callbacks } = this.props;
            const form = this.getFormFromTaskAttachmentRowItem(attachmentRowItem);
            form && callbacks.onOpenEditFormDialog(form);
        }, /**
     * Callback for removing a form from the task
     * @param attachmentRowItem form attachment to delete
     */ this.onDeleteFormAttachment = (attachmentRowItem)=>{
            const { callbacks } = this.props;
            const form = this.getFormFromTaskAttachmentRowItem(attachmentRowItem);
            form && callbacks.onOpenRemoveFormConfirmationDialog(form);
        }, /**
     * Copy the link for given attachment
     * @param attachmentRowItem Attachment row item to open link for
     */ this.onCopyFormAttachmentLink = (attachmentRowItem)=>{
            if (!attachmentRowItem.isBeingUploaded) {
                const formAttachment = this.getFormAttachmentFromTaskAttachmentRowItem(attachmentRowItem, FormUrlType.EditForm);
                formAttachment && this.props.callbacks.onCopyAttachmentLink(formAttachment);
            }
        }, /**
     * Completion Requirements the plan info callout
     */ this.toggleCompletionRequirementsInfoCallout = ()=>{
            this.setState((prevState)=>{
                return {
                    isCompletionRequirementsInfoCalloutVisible: !prevState.isCompletionRequirementsInfoCalloutVisible
                };
            });
        }, /**
     * Update the checklist on task details
     * @param updatedChecklist Updated checklist
     */ this.updateChecklist = (updatedChecklist)=>{
            const { taskDetails: currentTaskDetails, callbacks, config } = this.props;
            if (currentTaskDetails == null) {
                return;
            }
            let updatedTaskDetails = currentTaskDetails.setProperty("checklist", updatedChecklist);
            const compactCurrentChecklist = compact(values(currentTaskDetails.checklist));
            const compactUpdatedChecklist = compact(values(updatedChecklist));
            const enableMandatoryChecklistToggle = config.mandatoryChecklistTogglePermissions === MandatoryChecklistTogglePermissions.DisableNone;
            if (compactCurrentChecklist.length === 0 && compactUpdatedChecklist.length === 1 && currentTaskDetails.previewType === PreviewType.Automatic) {
                updatedTaskDetails = updatedTaskDetails.setProperty("previewType", PreviewType.CheckList);
            } else if (compactUpdatedChecklist.length === 0 && currentTaskDetails.previewType === PreviewType.CheckList) {
                updatedTaskDetails = updatedTaskDetails.setProperty("previewType", PreviewType.NoPreview);
            }
            this.setState((prevState)=>{
                return {
                    prevCheckListStats: this.getItemStats(currentTaskDetails.checklist)
                };
            });
            if (enableMandatoryChecklistToggle && this.state.isChecklistMandatory) {
                const updatedchecklistListIds = Object.keys(updatedChecklist);
                if (updatedchecklistListIds.length === 0) {
                    updatedTaskDetails = updatedTaskDetails.setProperty("completionRequirements", null);
                    this.setState({
                        isChecklistMandatory: false
                    });
                } else {
                    const checklistRequirement = new ChecklistCompletionRequirement(updatedchecklistListIds);
                    const comletionRequirements = CompletionRequirements.builder.withChecklistRequirement(checklistRequirement).build();
                    updatedTaskDetails = updatedTaskDetails.setProperty("completionRequirements", comletionRequirements);
                }
            }
            callbacks.onUpdateTaskDetails(currentTaskDetails, updatedTaskDetails);
        }, /**
     * On certain key strokes, we want to stop editing the task title
     * @param e The keyboard event that was fired
     */ this.taskTitleKeyPress = (e)=>{
            const keyPressed = KeyboardUtilities.getKey(e);
            if (keyPressed === KeyCode.ESCAPE || keyPressed === KeyCode.ENTER) {
                this.taskTitleUpdate(e);
                e.preventDefault();
            }
        }, /**
     * What to call when task title edit is completed
     * @param e The event that was fired
     */ this.taskTitleUpdate = (event)=>{
            const { task: currentTask, callbacks } = this.props;
            if (currentTask == null) {
                return;
            }
            if (event.target.value.trim() === "") {
                // if it's only white spaces, revert back to original title
                this.setState((prevState)=>{
                    return {
                        updatedTitle: currentTask.title
                    };
                });
                return;
            }
            const newTitle = event.target.value;
            // If the new title is the same as the current title, don't update
            if (newTitle === currentTask.title) {
                return;
            }
            const updatedTask = currentTask.setProperty("title", newTitle);
            callbacks.onUpdateTask(currentTask, updatedTask);
            callbacks.fetchFileSuggestions(updatedTask, this.props.taskDetails);
        }, /**
     * Update the state with freshly typed notes
     * @param notesContent New notes content
     */ this.onRichTextNotesChanged = (notesContent)=>{
            this.throttledTaskDescriptionUpdate(notesContent);
            this.setState((prevState)=>{
                return {
                    updatedNotesContent: notesContent
                };
            });
        }, /**
     * On certain key strokes, we want to stop editing the task description
     * @param e The keyboard event that was fired
     */ this.taskDescriptionKeyPress = (e)=>{
            if (KeyboardUtilities.getKey(e) === KeyCode.ESCAPE) {
                const targetElement = e.target;
                this.taskDescriptionUpdate(targetElement.value);
                const shouldAlertDescriptionBeUpdated = this.state.updatedDescription !== this.descriptionSinceLastUpdateAlert;
                if (shouldAlertDescriptionBeUpdated) {
                    this.alertUserDescriptionUpdated();
                }
                e.preventDefault();
            }
        }, /**
     * Enter description edit mode when focusing on the description
     * @param event Focus event
     */ this.onTaskDescriptionFocus = (event)=>{
            if (!this.state.showDescriptionInEditMode) {
                // Switch description to edit mode
                this.setState({
                    showDescriptionInEditMode: true
                }, //TODO add focus support in PWS rich text editor. #5787584
                ()=>{
                    // Reset focus on the description textfield after it is rendered
                    this.descriptionTextField?.focus();
                });
            }
        }, /**
     * Handles task notes blur event , here the value comes from the rich text editor, giving back its content.
     * @param event React focus event
     */ this.onTaskNotesBlur = (event, value)=>{
            // Cancel the throttled function invocation which may be pending if user loses focus from task notes before the end of wait period.
            // This is to avoid redundant service call because we anyways save the latest notes immediately on blur.
            this.throttledTaskDescriptionUpdate.cancel();
            // Save the latest task notes
            this.taskDescriptionOnBlurUpdate(value);
        }, /**
     * Handles task notes blur event for plain text editor
     * @param event React focus event
     */ this.onTaskDescriptionBlur = (event)=>{
            // Cancel the throttled function invocation which may be pending if user loses focus from task notes before the end of wait period.
            // This is to avoid redundant service call because we anyways save the latest notes immediately on blur.
            this.throttledTaskDescriptionUpdate.cancel();
            // Save the updated task description in task store and service.
            const targetElement = event.target;
            this.taskDescriptionOnBlurUpdate(targetElement.value);
        }, /**
     * Function to send alert and update the task details with latest description field
     * @param descriptionValue string
     */ this.taskDescriptionOnBlurUpdate = (descriptionValue)=>{
            // Save the latest task notes
            this.taskDescriptionUpdate(descriptionValue);
            const shouldAlertDescriptionBeUpdated = !this.props.config.enableRichTextEditor ? this.state.updatedDescription !== this.descriptionSinceLastUpdateAlert : this.state.updatedNotesContent !== this.notesContentSinceLastUpdateAlert;
            if (shouldAlertDescriptionBeUpdated) {
                this.alertUserDescriptionUpdated();
            }
            // Switch description to linkify mode
            if (this.state.showDescriptionInEditMode) {
                this.setState({
                    showDescriptionInEditMode: false
                });
            }
            if (this.props.taskDetails) {
                const updatedTaskDetails = this.props.config.enableRichTextEditor ? this.props.taskDetails.setProperty("notes", {
                    content: descriptionValue.trim(),
                    contentType: ContentType.HTML
                }) : this.props.taskDetails.setProperty("description", descriptionValue.trim());
                this.props.callbacks.fetchFileSuggestions(this.props.task, updatedTaskDetails);
            }
        }, /**
     * Callback to handle the selection of the bucket dropdown
     * @param event React Form event for the callback
     * @param option Selected dropdown option
     */ this.onBucketChanged = (event, option)=>{
            const { task: currentTask, callbacks } = this.props;
            const { engagementLogger } = this.context;
            const newBucketId = option.key.toString();
            if (currentTask == null) {
                return;
            }
            engagementLogger.logEngagement(this.name(), EngagementTelemetryConstants.TaskBucketUpdated);
            const updatedTask = currentTask.setProperty("bucketId", newBucketId !== "" ? newBucketId : null);
            callbacks.onUpdateTask(currentTask, updatedTask);
            callbacks.fetchFileSuggestions(updatedTask, this.props.taskDetails);
        }, /**
     * Callback to handle the selection of the progress dropdown
     * @param event React Form event for the callback
     * @param option selected dropdown option
     */ this.onProgressChanged = (event, option)=>{
            const { task: currentTask, callbacks } = this.props;
            const { engagementLogger } = this.context;
            const newProgressState = ProgressState[option.key];
            if (currentTask == null) {
                return;
            }
            engagementLogger.logEngagement(this.name(), EngagementTelemetryConstants.TaskProgressUpdated);
            const updatedTask = currentTask.setProperty("percentComplete", this.props.callbacks.convertProgressStateToPercentComplete(newProgressState));
            callbacks.onUpdateTask(currentTask, updatedTask);
        }, /**
     * Callback to handle the selection of the priority dropdown
     * @param event React Form event for the callback
     * @param option selected dropdown option
     */ this.onPriorityChanged = (event, option)=>{
            const { task: currentTask, callbacks } = this.props;
            const { engagementLogger, traceLogger } = this.context;
            const newPriorityName = PriorityName[option.key];
            if (currentTask == null) {
                return;
            }
            engagementLogger.logEngagement(this.name(), EngagementTelemetryConstants.TaskPriorityUpdated);
            const updatedTask = currentTask.setProperty("priority", this.props.callbacks.convertPriorityNameToPriority(newPriorityName, traceLogger));
            callbacks.onUpdateTask(currentTask, updatedTask);
        }, /**
     * Callback to handle the selection of the start date dropdown
     * @param newDate The new start date
     */ this.onStartDateChanged = (newDate)=>{
            const { task: currentTask, callbacks } = this.props;
            const { engagementLogger } = this.context;
            if (currentTask == null) {
                return;
            }
            engagementLogger.logEngagement(this.name(), EngagementTelemetryConstants.TaskStartDateUpdated);
            const updatedTask = currentTask.setStartDate(newDate == null ? null : moment(newDate));
            callbacks.onUpdateTask(currentTask, updatedTask);
        }, /**
     * Callback to handle the selection of the end date dropdown
     * @param newDate The new end date
     */ this.onEndDateChanged = (newDate)=>{
            const { task: currentTask, callbacks } = this.props;
            const { engagementLogger } = this.context;
            if (currentTask == null) {
                return;
            }
            if (newDate == null && currentTask.recurrence?.isActive()) {
                this.logUsage(EngagementTelemetryConstants.RecurrenceDueDateUpdated);
            }
            engagementLogger.logEngagement(this.name(), EngagementTelemetryConstants.TaskFinishDateUpdated);
            const updatedTask = currentTask.setDueDate(newDate == null ? null : moment(newDate));
            callbacks.onUpdateTask(currentTask, updatedTask);
        }, /**
     * Callback to handle the selection of an option from the recurrence dropdown
     * @param option Selected dropdown option
     */ this.onRecurrenceChanged = (event, option)=>{
            const { task: currentTask, callbacks } = this.props;
            const { traceLogger } = this.context;
            const recurrenceDropdownType = option.key;
            /*
            If the user selected the same dropdown, lets assume there should be no change.
            Unless the user selected custom, then the custom dialog should open.
        */ if (recurrenceDropdownType !== RecurrenceDropdownType.Custom && // Custom not selected
            this.getRecurrenceDropdownType() === recurrenceDropdownType // Recurrence option not changed
            ) {
                return;
            }
            if (recurrenceDropdownType === RecurrenceDropdownType.Custom) {
                callbacks.onOpenCustomRecurrenceDialog();
                return;
            }
            // Use task dueDate if possible, otherwise use task startDate, lastly use moment
            const patternDate = currentTask.dueDate ?? currentTask.startDate ?? ToUTC10am(moment());
            this.logUsage(EngagementTelemetryConstants.RecurrenceOptionSelected);
            let recurrencePattern = null;
            switch(recurrenceDropdownType){
                case RecurrenceDropdownType.NotRepeat:
                    break;
                case RecurrenceDropdownType.Daily:
                    recurrencePattern = RecurrencePattern.builder.withDaily().build();
                    break;
                case RecurrenceDropdownType.Weekdays:
                    recurrencePattern = RecurrencePattern.builder.withWeekly([
                        DayOfWeek.Monday,
                        DayOfWeek.Tuesday,
                        DayOfWeek.Wednesday,
                        DayOfWeek.Thursday,
                        DayOfWeek.Friday
                    ]).build();
                    break;
                case RecurrenceDropdownType.Weekly:
                    recurrencePattern = RecurrencePattern.builder.withWeekly([
                        getDayOfWeekFromMomentIndex(patternDate.day())
                    ]).build();
                    break;
                case RecurrenceDropdownType.Monthly:
                    recurrencePattern = RecurrencePattern.builder.withAbsoluteMonthly(patternDate.date()).build();
                    break;
                case RecurrenceDropdownType.Yearly:
                    recurrencePattern = RecurrencePattern.builder.withAbsoluteYearly(patternDate.date(), getRecurrenceMonthIndexFromMoment(patternDate)).build();
                    break;
                default:
                    ErrorUtilities.unreachableCase(recurrenceDropdownType, (log)=>{
                        traceLogger.logTrace(0x1e44900b /* tag_4rjal */ , TraceLevel.Warning, log);
                    });
                    break;
            }
            // Generate a new recurrence schedule
            let schedule = null; // Default to Termination
            if (recurrencePattern != null) {
                // We have a valid recurrence pattern, so create a schedule containing the pattern
                if (currentTask.recurrence?.schedule == null) {
                    // Recurrence creation or recreation, build out schedule with pattern defined above
                    schedule = RecurrenceSchedule.builder.withRecurrencePattern(recurrencePattern).build();
                } else {
                    // Recurrence schedule exists, update schedule with pattern defined above
                    schedule = currentTask.recurrence.schedule.setRecurrencePattern(recurrencePattern);
                }
            }
            const updatedTask = currentTask.setRecurrenceSchedule(schedule);
            callbacks.onUpdateTask(currentTask, updatedTask);
        }, /**
     * Helper method to stop propagation
     * @param evt
     */ this.stopPropagation = (evt)=>{
            if (KeyboardUtilities.getKey(evt) !== KeyCode.ESCAPE) {
                evt.stopPropagation();
            }
        };
        const { task, taskDetails, config } = props;
        const currentDescription = taskDetails?.description ?? null;
        const currentNotesContent = config.enableRichTextEditor ? taskDetails?.notes?.content : currentDescription;
        this.descriptionSinceLastUpdateAlert = taskDetails != null ? currentDescription : null;
        this.notesContentSinceLastUpdateAlert = taskDetails != null ? currentNotesContent : null;
        this.state = {
            isAddFormDialogVisible: false,
            isCompletionRequirementsInfoCalloutVisible: false,
            isChecklistMandatory: (props.config.mandatoryChecklistTogglePermissions !== MandatoryChecklistTogglePermissions.HideMandatoryToggle && // At creation time, the promoted property for specifiedCompletionRequirements on the task is not set yet.
            // So we need to check the task details instead.
            (!!areChecklistCompletionRequirementSet(task, taskDetails) || taskDetails?.hasMandatoryChecklist())) ?? false,
            updatedTitle: task ? task.title : "",
            updatedDescription: taskDetails != null ? currentDescription : null,
            updatedNotesContent: taskDetails != null ? currentNotesContent : null,
            showDescriptionInEditMode: false,
            descriptionWithHyperlinks: taskDetails?.description ? convertUrlsToRenderableLinks(taskDetails.description) : null,
            prevCheckListStats: null,
            hasInvalidNotes: false
        };
        // Use leading: false to prevent running update() on 1 letter descriptions when user begins typing
        this.throttledTaskDescriptionUpdate = throttle(this.taskDescriptionUpdate, ThrottleValueForTaskDescriptionUpdate, {
            leading: false
        });
        this.recurrenceOptionList = [
            {
                key: RecurrenceDropdownType.NotRepeat,
                text: Strings.TaskRecurrence_TaskRecurrenceState_DoesNotRepeat
            },
            {
                key: RecurrenceDropdownType.Daily,
                text: Strings.TaskRecurrence_TaskRecurrenceState_Daily
            },
            {
                key: RecurrenceDropdownType.Weekdays,
                text: Strings.TaskRecurrence_TaskRecurrenceState_Weekdays
            },
            {
                key: RecurrenceDropdownType.Weekly,
                text: Strings.TaskRecurrence_TaskRecurrenceState_Weekly
            },
            {
                key: RecurrenceDropdownType.Monthly,
                text: Strings.TaskRecurrence_TaskRecurrenceState_Monthly
            },
            {
                key: RecurrenceDropdownType.Yearly,
                text: Strings.TaskRecurrence_TaskRecurrenceState_Yearly
            },
            {
                key: RecurrenceDropdownType.Custom,
                text: Strings.TaskRecurrence_TaskRecurrenceState_Custom
            }
        ];
    }
}
/** Debounce interval for task updates called from dropdown components in ms */ TaskEditor.debounceDropdownTaskUpdateInterval = 200;
// eslint-disble-next-line import/no-default-export
export default withDndContext(TaskEditor, ()=>Promise.resolve());
